import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FileText, Save, Trash2, Wand2, Info, AlertCircle, AlertTriangle, Users, Settings, Plus, Merge, Edit, ChevronDown, ChevronUp, ChevronRight, FolderOpen, GripVertical, MessageCircle, Folder, BarChart3, Play, Clock, Eye, Download, X } from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import api from '../../api';
import toast from 'react-hot-toast';
import { toastService } from '../../services/toastService';
import Tooltip from './Tooltip';
import TerminologyTooltip from '../TerminologyTooltip';
import EnhancedStepChatModal from '../EnhancedStepChatModal';
import SOPContentRenderer from './SOPContentRenderer';
import { getStepNumber } from '../../utils/stepNumbering';
import RichTextEditor from '../RichTextEditor';
import AddSectionModal from '../AddSectionModal';
import OutOfSyncWarning from '../SyncStatus/OutOfSyncWarning';

type StepObj = { step: string; sources?: any[]; inference?: string; is_expanded?: boolean };
function isStepObj(val: unknown): val is StepObj {
  return typeof val === 'object' && val !== null && 'step' in val;
}

interface AnalysisProgress {
  progress: number;
  completed: number;
  failed: number;
  pending: number;
  analyzing: number;
  total: number;
}

interface ProcessStep {
  step: string;
  sources?: any[];
  inference?: string;
  is_expanded?: boolean;
  parent_step_index?: number;
  conversation_derived?: boolean;
  created_at?: string;
  section_id?: string;
}

interface ProcessSection {
  id: string;
  name: string;
  description?: string;
  color: string;
  order: number;
  step_count?: number;
}



const SOPGenerationTab: React.FC<{
  analysisResults: any;
  loadAnalysisResults: () => Promise<void>;
  isAnalyzing: boolean;
  analysisProgress: AnalysisProgress | null;
  globalComplianceScore: number | null;
  onRefresh?: () => void;
}> = ({ analysisResults, loadAnalysisResults, isAnalyzing, analysisProgress, globalComplianceScore, onRefresh }) => {

  // Early returns MUST come before any hooks to avoid violating Rules of Hooks
  if (!analysisResults) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="max-w-md mx-auto items-center justify-center flex flex-col">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">No Analysis Data</h3>
          <p className="text-gray-600 mb-4">Please run document analysis first.</p>
          <button
            onClick={onRefresh}
            className="bg-electric-blue text-white px-6 py-2 rounded-lg hover:bg-darker-blue transition-colors flex items-center space-x-2 mx-auto"
          >
            <Play className="w-4 h-4" />
            <span>Start Analysis</span>
          </button>
        </div>
      </div>
    );
  }

  // Check 2: Documents uploaded but not analyzed
  const documents = analysisResults?.documents || [];
  const documentAnalyses = analysisResults?.document_analyses || [];

  if (documents.length > 0 && documentAnalyses.length === 0) {
    return (
      <div className="text-center py-12 bg-amber-50 rounded-lg border-2 border-dashed border-amber-300">
        <div className="max-w-md mx-auto">
          <FileText className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">Documents Need Analysis</h3>
          <p className="text-gray-600 mb-4">
            You have {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded, but they haven't been analyzed yet.
          </p>
          <button
            onClick={onRefresh}
            className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition-colors flex items-center space-x-2 mx-auto"
          >
            <Play className="w-4 h-4" />
            <span>Start Analysis</span>
          </button>
        </div>
      </div>
    );
  }

  const synthesis = analysisResults?.process_synthesis;

  // Check 3: Documents analyzed but no synthesis
  if (documentAnalyses.length > 0 && !synthesis) {
    return (
      <div className="text-center py-12 bg-purple-50 rounded-lg border-2 border-dashed border-purple-300">
        <div className="max-w-md mx-auto">
          <FileText className="w-16 h-16 text-purple-500 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">Process Synthesis Required</h3>
          <p className="text-gray-600 mb-4">
            Documents have been analyzed, but process synthesis hasn't been completed yet. SOP generation requires process synthesis.
          </p>
          {/* <button
            onClick={onRefresh}
            className="bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600 transition-colors flex items-center space-x-2 mx-auto"
          >
            <BarChart3 className="w-4 h-4" />
            <span>Start Process Synthesis</span>
          </button> */}
        </div>
      </div>
    );
  }

  // Check 4: Synthesis complete but no SOP - Show loader instead of card to prevent multiple clicks
  if (synthesis && !analysisResults?.sop && !analysisResults?.sops?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-electric-blue border-t-transparent rounded-full animate-spin"></div>
          <FileText className="w-8 h-8 text-electric-blue absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-2">SOP Generation in Progress</h3>
        <p className="text-gray-600 text-center max-w-md">
          Please wait while we generate your Standard Operating Procedure. This may take a few moments...
        </p>
        <div className="mt-4 flex space-x-1">
          <div className="w-2 h-2 bg-electric-blue rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
          <div className="w-2 h-2 bg-electric-blue rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-2 h-2 bg-electric-blue rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      </div>
    );
  }

  {/* Previous card implementation - Commented out to show loader during generation
  if (synthesis && !sop) {
    return (
      <div className="text-center py-12 bg-green-50 rounded-lg border-2 border-dashed border-green-300">
        <div className="max-w-md mx-auto">
          <FileText className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">Ready for SOP Generation</h3>
          <p className="text-gray-600 mb-4">
            Process synthesis is complete. You can now generate your Standard Operating Procedure.
          </p>
          <button
            onClick={onRefresh}
            className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-2 mx-auto"
          >
            <FileText className="w-4 h-4" />
            <span>Generate SOP</span>
          </button>
        </div>
      </div>
    );
  }
  */}

  // All hooks must be called after early returns to avoid Rules of Hooks violation
  const { currentProject } = useProject();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [sopProgress, setSopProgress] = useState<{
    stage: string;
    progress: number;
    estimatedTime: number;
    startTime: number;
  } | null>(null);
  const [sopType, setSopType] = useState<'basic' | 'enhanced'>('basic');
  const [activeSOPTab, setActiveSOPTab] = useState<'basic' | 'enhanced' | 'process_map'>('process_map'); // Active tab
  const [isBackgroundProcessingDetected, setIsBackgroundProcessingDetected] = useState(false);

  // Toggle view mode for transparency (Show All / Original Only / Additions Only)
  const [contentViewMode, setContentViewMode] = useState<'all' | 'original' | 'additions'>('all');
  const [isEvidenceExpanded, setIsEvidenceExpanded] = useState(false);
  const backgroundPollingRef = useRef<NodeJS.Timeout | null>(null);

  // Editable steps state (new logic)
  const [isSaving, setIsSaving] = useState(false);
  const [saveQueued, setSaveQueued] = useState(false);
  const saveTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const sopPollIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const [chatStepIdx, setChatStepIdx] = useState<number | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [isExportingSOP, setIsExportingSOP] = useState(false);

  // Stakeholder and Systems state (matching Process Synthesis design)
  const [stakeholders, setStakeholders] = useState<{ [key: string]: any }>({});
  const [editingStakeholder, setEditingStakeholder] = useState<string | null>(null);
  const [editStakeholderValue, setEditStakeholderValue] = useState({ role: '', responsibility: '' });
  const [showAddStakeholder, setShowAddStakeholder] = useState(false);
  const [newStakeholder, setNewStakeholder] = useState({ role: '', responsibility: '' });
  const [selectedStakeholders, setSelectedStakeholders] = useState<string[]>([]);
  const [isStakeholdersCollapsed, setIsStakeholdersCollapsed] = useState(false);

  // Terminology definitions state
  const [terminologyDefinitions, setTerminologyDefinitions] = useState<any[]>([]);
  const [isLoadingTerminology, setIsLoadingTerminology] = useState(false);

  const [systems, setSystems] = useState<{ [key: string]: any }>({});
  const [editingSystem, setEditingSystem] = useState<string | null>(null);
  const [editSystemValue, setEditSystemValue] = useState({ system: '', purpose: '' });
  const [showAddSystem, setShowAddSystem] = useState(false);
  const [newSystem, setNewSystem] = useState({ system: '', purpose: '' });
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  const [isSystemsCollapsed, setIsSystemsCollapsed] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionDescription, setNewSectionDescription] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [steps, setSteps] = useState<(string | ProcessStep)[]>([]);
  const [sections, setSections] = useState<ProcessSection[]>([]);
  const [sectionsMap, setSectionsMap] = useState<Map<string, string>>(new Map());
  const [sectionStepOrder, setSectionStepOrder] = useState<Map<string, number[]>>(new Map());
  const [draggedStepId, setDraggedStepId] = useState<number | null>(null);
  const [draggedFromSection, setDraggedFromSection] = useState<string | null>(null);
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [unassignedStepsExpanded, setUnassignedStepsExpanded] = useState(false); // Changed to false - collapsed by default
  const [isSectionManagementCollapsed, setIsSectionManagementCollapsed] = useState(true); // Section Management collapsed by default
  const [isEnhancingInProgress, setIsEnhancingInProgress] = useState(false);
  const [isBasicSOPGenerating, setIsBasicSOPGenerating] = useState(false);
  const [isRegeneratingFromMap, setIsRegeneratingFromMap] = useState(false);
  const [processMapId, setProcessMapId] = useState<number | null>(null);
  const [isStatusMessageDismissed, setIsStatusMessageDismissed] = useState(false);
  const [hasRACIData, setHasRACIData] = useState<boolean>(false);

  // REQ-SOP-2.2: Inline editing state
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [editedSections, setEditedSections] = useState<{[key: number]: {content: string, status: 'ai_generated' | 'user_modified' | 'user_approved'}}>({});
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(null);

  // REQ-SOP-2.3: Custom section addition state
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [insertAfterIndex, setInsertAfterIndex] = useState<number | null>(null);
  const sopRef = useRef<any>(null); // Initialize with null, will be updated in useEffect

  // Check if RACI data exists for smart button behavior
  useEffect(() => {
    console.log('🔍 [RACI Check] useEffect triggered, currentProject:', currentProject?.id);

    const checkRACIData = async () => {
      if (!currentProject?.id) {
        console.log('⚠️ [RACI Check] No currentProject.id, skipping API call');
        return;
      }

      console.log(`🌐 [RACI Check] Calling API: /api/raci/${currentProject.id}`);
      try {
        const response = await api.get(`/api/raci/${currentProject.id}`);
        console.log('✅ [RACI Check] API Response:', response.data);

        if (response.data.success && response.data.data) {
          const { businessUnits, stakeholderRoles, assignments } = response.data.data;
          // Check if there are any assignments (either businessUnit or stakeholderRole)
          const hasData = (assignments?.businessUnit && Object.keys(assignments.businessUnit).length > 0) ||
                         (assignments?.stakeholderRole && Object.keys(assignments.stakeholderRole).length > 0);
          console.log(`📊 [RACI Check] Has RACI data: ${hasData}`);
          setHasRACIData(hasData);
        } else {
          console.log('📭 [RACI Check] No data in response');
          setHasRACIData(false);
        }
      } catch (error) {
        console.log('❌ [RACI Check] No RACI data exists yet:', error);
        setHasRACIData(false);
      }
    };

    checkRACIData();
  }, [currentProject?.id]);

  // Enhanced effect to load steps from Enhanced Process Synthesis Tab
  useEffect(() => {

    let stepsArr: (string | StepObj)[] | undefined;


    // Load sections from backend
    if (synthesis?.sections && Array.isArray(synthesis.sections) && synthesis.sections.length > 0) {
      setSections(synthesis.sections);
    }

    // Load sections map from backend
    if (synthesis?.sections_map) {
      const newSectionsMap = new Map();
      Object.entries(synthesis.sections_map).forEach(([key, value]) => {
        newSectionsMap.set(key, value as string);
      });
      setSectionsMap(newSectionsMap);
    }

    // Load section step order from backend
    if (synthesis?.section_step_order) {
      const orderMap = new Map();
      Object.entries(synthesis.section_step_order).forEach(([sectionId, order]) => {
        if (Array.isArray(order)) {
          orderMap.set(sectionId, order);
        }
      });
      setSectionStepOrder(orderMap);
    }

    if (synthesis?.process_steps && Array.isArray(synthesis.process_steps) && synthesis.process_steps.length > 0) {
      synthesis.process_steps.slice(0, 3).map((s: any, i: number) => ({
        index: i,
        step: typeof s === 'string' ? s.substring(0, 80) + '...' : s?.step?.substring(0, 80) + '...'
      }));
      stepsArr = synthesis.process_steps;
    }


    else if (synthesis?.synthesis_text) {
      console.log('📥 SOPGenerationTab: Falling back to synthesis_text parsing...');
      let synthesisData;
      try {
        synthesisData = typeof synthesis.synthesis_text === 'string'
          ? JSON.parse(synthesis.synthesis_text)
          : synthesis.synthesis_text;
      } catch (error) {
        console.error('SOPGenerationTab: Failed to parse synthesis_text:', error);
        synthesisData = synthesis.synthesis_text;
      }

      if (synthesisData?.end_to_end_process && Array.isArray(synthesisData.end_to_end_process)) {
        console.log('📥 SOPGenerationTab: Using end_to_end_process fallback:', synthesisData.end_to_end_process.length, 'steps');
        stepsArr = synthesisData.end_to_end_process;
      }
    }

    if (stepsArr && stepsArr.length > 0) {
      console.log('✅ SOPGenerationTab: Setting steps from:', {
        source: synthesis?.process_steps?.length > 0 ? 'synthesis.process_steps (Enhanced Tab)' : 'synthesis_text (Fallback)',
        count: stepsArr.length,
        firstStep: typeof stepsArr[0] === 'string' ? stepsArr[0].substring(0, 100) : stepsArr[0]?.step?.substring(0, 100),
        timestamp: new Date().toISOString()
      });

      // Update steps state
      setSteps(stepsArr);
    } else {
      setSteps([]);
    }
  }, [
    // Primary process steps dependencies  
    synthesis?.process_steps,
    synthesis?.synthesis_text,

    // Synthesis object dependencies
    synthesis,
    synthesis?.id,
    synthesis?.updated_at,

    // Parent analysis results dependencies to catch refresh triggers
    analysisResults,
    analysisResults?.process_synthesis,
    analysisResults?.updated_at,
    analysisResults?.timestamp,

    // Deep change detection for process steps array
    JSON.stringify(synthesis?.process_steps)
  ]);

  const addSection = useCallback(async () => {
    if (!newSectionName.trim() || !currentProject?.id) return;

    try {

      const newSection: ProcessSection = {
        id: `section_${Date.now()}`,
        name: newSectionName.trim(),
        description: newSectionDescription.trim(),
        color: `#${Math.floor(Math.random() * 16777215).toString(16)}`,
        order: sections.length,
        step_count: 0
      };

      const updatedSections = [...sections, newSection];
      setSections(updatedSections);

      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: updatedSections,
        sectionsMap: Object.fromEntries(sectionsMap),
        business_type: synthesis?.business_type || 'general'
      });

      if (onRefresh) {
        console.log('🔄 Triggering global refresh after section add...');
        await onRefresh();
      }

      setNewSectionName('');
      setNewSectionDescription('');
      setShowAddSection(false);
      toast.success('Section added successfully');
    } catch (error) {
      console.error('❌ Error adding section:', error);
      toast.error('Failed to add section');

      // Revert local state on error
      setSections(sections);
    }
  }, [newSectionName, newSectionDescription, currentProject?.id, sections, sectionsMap, synthesis?.business_type, onRefresh]);

  const deleteSection = useCallback(async (sectionId: string) => {
    if (!currentProject?.id) return;

    try {
      console.log('🗑️ Deleting section:', sectionId);

      // Remove section
      const updatedSections = sections.filter(s => s.id !== sectionId);
      setSections(updatedSections);

      // Remove assignments from sectionsMap
      const newSectionsMap = new Map();
      sectionsMap.forEach((assignedSectionId, stepIndex) => {
        if (assignedSectionId !== sectionId) {
          newSectionsMap.set(stepIndex, assignedSectionId);
        }
      });
      setSectionsMap(newSectionsMap);

      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: updatedSections,
        sectionsMap: Object.fromEntries(newSectionsMap),
        business_type: synthesis?.business_type || 'general'
      });

      if (onRefresh) {
        await onRefresh();
      }

      toast.success('Section deleted successfully');
    } catch (error) {
      toast.error('Failed to delete section');

      // Revert local state on error
      setSections(sections);
      setSectionsMap(sectionsMap);
    }
  }, [currentProject?.id, sections, sectionsMap, synthesis?.business_type, onRefresh]);


  
  // Step management functions
  const deleteStep = async (stepIndex: number) => {
    if (!currentProject?.id) {
      toast.error('No project selected');
      return;
    }

    try {
      const updatedSteps = steps.filter((_, index) => index !== stepIndex);
      setSteps(updatedSteps);

      // Update sections map - shift indices for steps after deleted step
      const newSectionsMap = new Map();
      sectionsMap.forEach((sectionId, indexStr) => {
        const index = parseInt(indexStr);
        if (index < stepIndex) {
          newSectionsMap.set(indexStr, sectionId);
        } else if (index > stepIndex) {
          newSectionsMap.set((index - 1).toString(), sectionId);
        }
      });
      setSectionsMap(newSectionsMap);

      // CRITICAL: Save steps to backend
      await api.put(`/api/analysis/process-steps/${currentProject.id}`, {
        steps: updatedSteps
      });

      // Save sections map to backend if we have sections
      if (sections.length > 0) {
        await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
          sections: sections,
          sectionsMap: Object.fromEntries(newSectionsMap),
          business_type: synthesis?.business_type || 'general'
        });
      }

      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 200);
      }

      toast.success('Step deleted and synchronized across tabs', {
        duration: 3000,
        icon: '🗑️'
      });

    } catch (error) {
      toast.error('Failed to delete step. Please try again.');

      // Revert local state on error
      setSteps(steps);
      setSectionsMap(sectionsMap);
    }
  };

  useEffect(() => {
    // Load sections from backend
    if (synthesis?.sections && Array.isArray(synthesis.sections) && synthesis.sections.length > 0) {
      console.log('📥 SOPGenerationTab: Loading sections from backend:', synthesis.sections.length, 'sections');
      setSections(synthesis.sections);
    }

    // Load sections map from backend
    if (synthesis?.sections_map) {
      const newSectionsMap = new Map();
      Object.entries(synthesis.sections_map).forEach(([key, value]) => {
        newSectionsMap.set(key, value as string);
      });
      setSectionsMap(newSectionsMap);
    }

    // Load section step order from backend
    if (synthesis?.section_step_order) {
      const orderMap = new Map();
      Object.entries(synthesis.section_step_order).forEach(([sectionId, order]) => {
        if (Array.isArray(order)) {
          orderMap.set(sectionId, order);
        }
      });
      setSectionStepOrder(orderMap);
    }
  }, [
    synthesis?.sections,
    synthesis?.sections_map,
    synthesis?.section_step_order,
    JSON.stringify(synthesis?.sections),
    JSON.stringify(synthesis?.sections_map),
    JSON.stringify(synthesis?.section_step_order)
  ]);

  const moveStepToSection = useCallback(async (stepIndex: number, targetSectionId: string) => {
    if (!currentProject?.id) return;

    try {
      console.log('🔄 Moving step', stepIndex, 'to section', targetSectionId);

      // Update local sectionsMap immediately
      const newSectionsMap = new Map(sectionsMap);
      newSectionsMap.set(stepIndex.toString(), targetSectionId);
      setSectionsMap(newSectionsMap);

      // IMPORTANT: Also update section_step_order to include this step at the end of target section
      const newSectionStepOrder = new Map(sectionStepOrder);
      const targetSectionOrder = newSectionStepOrder.get(targetSectionId) || [];

      // Remove step from any existing section orders
      newSectionStepOrder.forEach((order, sectionId) => {
        const filteredOrder = order.filter(idx => idx !== stepIndex);
        newSectionStepOrder.set(sectionId, filteredOrder);
      });

      // Add step to end of target section
      const updatedTargetOrder = [...targetSectionOrder.filter(idx => idx !== stepIndex), stepIndex];
      newSectionStepOrder.set(targetSectionId, updatedTargetOrder);
      setSectionStepOrder(newSectionStepOrder);

      // Save both maps to backend
      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: sections,
        sectionsMap: Object.fromEntries(newSectionsMap),
        section_step_order: Object.fromEntries(newSectionStepOrder),  // ✅ Include this!
        business_type: synthesis?.business_type || 'general'
      });

      console.log('✅ Step moved and section order updated');

      if (onRefresh) {
        setTimeout(() => onRefresh(), 200);
      }

      toast.success('Step moved successfully', {
        duration: 2000,
        icon: '📁'
      });

    } catch (error) {
      toast.error('Failed to move step');

      // Revert local state on error
      setSectionsMap(sectionsMap);
      setSectionStepOrder(sectionStepOrder);
    }
  }, [currentProject?.id, sectionsMap, sectionStepOrder, sections, synthesis?.business_type, onRefresh]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Get unassigned steps
  const unassignedSteps = useMemo(() => {
    return steps
      .map((step, index) => ({ step, index }))
      .filter(({ index }) => !sectionsMap.has(index.toString()));
  }, [steps, sectionsMap]);

  // Group steps by section
  const stepsBySection = useMemo(() => {
    const grouped = new Map<string, Array<{ step: string | ProcessStep; index: number }>>();

    sections.forEach(section => {
      grouped.set(section.id, []);
    });

    // Get all steps that belong to sections
    const stepsInSections: Array<{ step: string | ProcessStep; index: number; sectionId: string }> = [];

    steps.forEach((step, index) => {
      const sectionId = sectionsMap.get(index.toString());
      if (sectionId && grouped.has(sectionId)) {
        stepsInSections.push({ step, index, sectionId });
      }
    });

    // Group steps by section and apply custom ordering
    stepsInSections.forEach(({ step, index, sectionId }) => {
      grouped.get(sectionId)!.push({ step, index });
    });

    // Apply custom ordering if it exists
    sections.forEach(section => {
      const sectionSteps = grouped.get(section.id) || [];
      const customOrder = sectionStepOrder.get(section.id);

      if (customOrder && customOrder.length > 0) {
        // Apply custom order for steps that are in the custom order
        const reorderedSteps: Array<{ step: string | ProcessStep; index: number }> = [];

        // First, add steps in their custom order
        customOrder.forEach(originalIndex => {
          const stepData = sectionSteps.find(s => s.index === originalIndex);
          if (stepData) {
            reorderedSteps.push(stepData);
          }
        });

        // Then, add any NEW steps that aren't in the custom order yet (append to end)
        sectionSteps.forEach(stepData => {
          if (!customOrder.includes(stepData.index)) {
            reorderedSteps.push(stepData);
          }
        });

        grouped.set(section.id, reorderedSteps);

        // Update the custom order to include the new steps
        if (reorderedSteps.length !== customOrder.length) {
          const newOrder = reorderedSteps.map(s => s.index);

          const newSectionStepOrder = new Map(sectionStepOrder);
          newSectionStepOrder.set(section.id, newOrder);
          setSectionStepOrder(newSectionStepOrder);
        }
      } else {
        sectionSteps.sort((a, b) => a.index - b.index);
        grouped.set(section.id, sectionSteps);

        // Create initial custom order based on current index order
        if (sectionSteps.length > 0) {
          const initialOrder = sectionSteps.map(s => s.index);
          const newSectionStepOrder = new Map(sectionStepOrder);
          newSectionStepOrder.set(section.id, initialOrder);
          setSectionStepOrder(newSectionStepOrder);
        }
      }
    });

    return grouped;
  }, [steps, sections, sectionsMap, sectionStepOrder]);

  useEffect(() => {
    const handleFocus = async () => {
      if (currentProject?.id && onRefresh) {

        setTimeout(() => {
          onRefresh();
        }, 500);
      }
    };

    // Listen for window focus events
    window.addEventListener('focus', handleFocus);

    // Also listen for tab visibility changes
    const handleVisibilityChange = () => {
      if (!document.hidden && currentProject?.id && onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentProject?.id, onRefresh]);

  useEffect(() => {
    if (currentProject?.id && onRefresh) {
      onRefresh();
    }
  }, []);

  const getProperStepNumber = (stepIndex: number) => {
    // Use the original step index to get the proper step number
    return getStepNumber(steps, stepIndex);
  };

  const handleSectionStepDragStart = (e: React.DragEvent, stepIndex: number, sectionId: string) => {

    setDraggedStepId(stepIndex);
    setDraggedFromSection(sectionId);

    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'section-step',
      stepIndex: stepIndex,
      sectionId: sectionId,
      source: 'section'
    }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionContainerDrop = async (e: React.DragEvent, targetSectionId: string) => {
    e.preventDefault();

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));

      if (dragData.type === 'main-flow-step' || dragData.type === 'section-step') {
        // This is moving a step TO a section (from main flow or from another section)
        await moveStepToSection(dragData.stepIndex, targetSectionId);
      }
    } catch (error) {
      console.error('❌ Error in section container drop:', error);
    } finally {
      setDraggedStepId(null);
      setDraggedFromSection(null);
    }
  };

  const handleSectionStepDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionStepDrop = async (e: React.DragEvent, targetStepIndex: number, targetSectionId: string) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent the container drop from also firing

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));

      if (dragData.type === 'section-step' && dragData.sectionId === targetSectionId) {
        // This is reordering within the same section
        if (dragData.stepIndex !== targetStepIndex) {
          await reorderStepsInSection(dragData.stepIndex, targetStepIndex, targetSectionId);
        }
      } else if (dragData.type === 'main-flow-step' ||
        (dragData.type === 'section-step' && dragData.sectionId !== targetSectionId)) {
        // This is moving a step TO this section
        await moveStepToSection(dragData.stepIndex, targetSectionId);
      }
    } catch (error) {
      console.error('❌ Error in section step drop:', error);
    } finally {
      setDraggedStepId(null);
      setDraggedFromSection(null);
    }
  };


  const reorderStepsInSection = useCallback(async (fromStepIndex: number, toStepIndex: number, sectionId: string) => {
    if (!currentProject?.id) return;



    try {
      // Get current section steps in their display order
      const currentSectionSteps = stepsBySection.get(sectionId) || [];

      const stepIndices = currentSectionSteps.map(s => s.index);

      // Find positions of the steps we're reordering
      const fromPosition = stepIndices.indexOf(fromStepIndex);
      const toPosition = stepIndices.indexOf(toStepIndex);

      if (fromPosition === -1 || toPosition === -1) {
        console.error('❌ Could not find step positions');
        return;
      }

      // Create new order by moving the step
      const newOrder = [...stepIndices];
      const [movedStepIndex] = newOrder.splice(fromPosition, 1);
      newOrder.splice(toPosition, 0, movedStepIndex);

      const newSectionStepOrder = new Map(sectionStepOrder);
      newSectionStepOrder.set(sectionId, newOrder);
      setSectionStepOrder(newSectionStepOrder);

      // Save to backend immediately
      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: sections,
        sectionsMap: Object.fromEntries(sectionsMap),
        business_type: synthesis?.business_type || 'general',
        section_step_order: Object.fromEntries(newSectionStepOrder)
      });

      // Trigger global refresh to sync all tabs
      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 200);
      }

      toast.success('Steps reordered successfully', {
        duration: 2000,
        icon: '🔄'
      });

    } catch (error) {
      console.error('❌ Error reordering steps in section:', error);
      toast.error('Failed to reorder steps');

      // Revert local state on error
      setSectionStepOrder(sectionStepOrder);
    }
  }, [currentProject?.id, stepsBySection, sectionStepOrder, sections, sectionsMap, synthesis?.business_type, onRefresh]);

  const handleMainFlowDragStart = (e: React.DragEvent, stepIndex: number) => {

    setDraggedStepId(stepIndex);
    setDraggedFromSection('main-flow');

    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'main-flow-step',
      stepIndex: stepIndex,
      source: 'main-flow'
    }));
    e.dataTransfer.effectAllowed = 'move';
  };


  useEffect(() => {
    if (synthesis?.section_step_order) {
      const orderMap = new Map();
      Object.entries(synthesis.section_step_order).forEach(([sectionId, order]) => {
        if (Array.isArray(order)) {
          orderMap.set(sectionId, order);
        }
      });
      setSectionStepOrder(orderMap);

    }
  }, [synthesis?.section_step_order]);


  const saveSectionOrder = useCallback(async (newSectionStepOrder: Map<string, number[]>) => {
    if (!currentProject?.id) return;

    try {
      const sectionOrderObj = Object.fromEntries(
        Array.from(newSectionStepOrder.entries()).map(([secId, order]) => [secId, order])
      );

      await api.put(`/api/analysis/process-synthesis/${currentProject.id}/sections`, {
        sections: sections,
        sectionsMap: Object.fromEntries(sectionsMap),
        section_step_order: sectionOrderObj,
        business_type: synthesis?.business_type || 'general'
      });

    } catch (error) {
      console.error('❌ Error saving section order:', error);
    }
  }, [currentProject?.id, sections, sectionsMap, synthesis?.business_type]);

  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const debouncedSaveSectionOrder = useCallback(
    debounce((newOrder: Map<string, number[]>) => {
      saveSectionOrder(newOrder);
    }, 500),
    [saveSectionOrder]
  );


  useEffect(() => {
    const synthesisText = synthesis?.synthesis_text;
    let synthesisData;
    try {
      synthesisData = typeof synthesisText === 'string' ?
        JSON.parse(synthesisText) : synthesisText;
    } catch {
      synthesisData = synthesis;
    }

    if (synthesisData?.stakeholders) {
      setStakeholders(synthesisData.stakeholders);
    }

    // IMPROVED: Handle different formats of systems_platforms
    if (synthesisData?.systems_platforms) {
      console.log('Loading systems from synthesis:', synthesisData.systems_platforms);

      let systemsData: { [key: string]: any } = {};

      // Handle object format (most common)
      if (typeof synthesisData.systems_platforms === 'object' && synthesisData.systems_platforms !== null && !Array.isArray(synthesisData.systems_platforms)) {
        // If it's already an object, ensure values are strings
        Object.entries(synthesisData.systems_platforms).forEach(([key, value]: [string, any]) => {
          if (typeof value === 'string') {
            systemsData[key] = value;
          } else if (typeof value === 'object' && value !== null) {
            // If value is an object, extract meaningful text
            systemsData[key] = value.purpose || value.description || value.name || JSON.stringify(value);
          } else {
            systemsData[key] = String(value);
          }
        });
      }
      // Handle array format
      else if (Array.isArray(synthesisData.systems_platforms)) {
        synthesisData.systems_platforms.forEach((system: any, index: number) => {
          if (typeof system === 'string') {
            systemsData[system] = 'System component';
          } else if (typeof system === 'object' && system !== null) {
            const key = system.name || system.system || `System ${index + 1}`;
            const value = system.purpose || system.description || 'System component';
            systemsData[key] = value;
          } else {
            systemsData[`System ${index + 1}`] = String(system);
          }
        });
      }
      // Handle string format (JSON string)
      else if (typeof synthesisData.systems_platforms === 'string') {
        try {
          const parsed = JSON.parse(synthesisData.systems_platforms);
          if (typeof parsed === 'object' && parsed !== null) {
            Object.entries(parsed).forEach(([key, value]: [string, any]) => {
              systemsData[key] = typeof value === 'string' ? value : String(value);
            });
          }
        } catch (e) {
          console.warn('Failed to parse systems_platforms string:', e);
          systemsData = { [synthesisData.systems_platforms]: 'System component' };
        }
      }

      setSystems(systemsData);
    }

    // Also try loading from direct synthesis fields as fallback
    if ((!synthesisData?.systems_platforms || Object.keys(synthesisData.systems_platforms || {}).length === 0) && synthesis?.systems_platforms) {
      console.log('Using fallback systems_platforms from direct synthesis field');
      let systemsData: { [key: string]: any } = {};

      if (typeof synthesis.systems_platforms === 'object' && synthesis.systems_platforms !== null) {
        Object.entries(synthesis.systems_platforms).forEach(([key, value]: [string, any]) => {
          systemsData[key] = typeof value === 'string' ? value : String(value);
        });
        setSystems(systemsData);
      }
    }
  }, [synthesis]);

  // Save steps to backend (debounced)
  const saveSteps = useCallback((newSteps: (string | StepObj)[]) => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    setSaveQueued(true);
    saveTimeout.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        const projectId = currentProject?.id || synthesis?.project_id;
        if (!projectId) {
          console.error('No project ID available for saving steps');
          toast.error('Unable to save - no project selected');
          return;
        }
        console.log('Saving steps to backend:', { projectId, stepsCount: newSteps.length, steps: newSteps });
        const response = await api.put(`/api/analysis/process-steps/${projectId}`, { steps: newSteps });
        console.log('Steps saved successfully:', response.data);
        console.log('🔄 Refreshing parent data to sync across tabs...');

        // Refresh parent data so other tabs see the updated steps
        if (onRefresh) {
          onRefresh();
        }

        toast.success('Process steps updated successfully');
      } catch (err) {
        console.error('Failed to save steps:', err);
        toast.error('Failed to save process steps. Please try again.');
      } finally {
        setIsSaving(false);
        setSaveQueued(false);
      }
    }, 300);
  }, [currentProject?.id, synthesis, onRefresh]);

  // Stakeholder management functions
  const saveStakeholders = useCallback(async (newStakeholders: { [key: string]: any }) => {
    try {
      const projectId = currentProject?.id || synthesis?.project_id;
      if (!projectId) return;
      await api.put(`/api/analysis/stakeholders/${projectId}`, { stakeholders: newStakeholders });
      console.log('✅ Stakeholders saved successfully');
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to save stakeholders:', err);
      toast.error('Failed to save stakeholders');
    }
  }, [currentProject?.id, synthesis, onRefresh]);

  const startEditStakeholder = (role: string) => {
    setEditingStakeholder(role);
    setEditStakeholderValue({ role, responsibility: stakeholders[role] });
  };

  const saveStakeholderEdit = () => {
    if (!editingStakeholder) return;
    const updated = { ...stakeholders };
    if (editStakeholderValue.role !== editingStakeholder) {
      delete updated[editingStakeholder];
    }
    updated[editStakeholderValue.role] = editStakeholderValue.responsibility;
    setStakeholders(updated);
    setEditingStakeholder(null);
    saveStakeholders(updated);
  };

  const deleteStakeholder = (role: string) => {
    const updated = { ...stakeholders };
    delete updated[role];
    setStakeholders(updated);
    setSelectedStakeholders(selectedStakeholders.filter(r => r !== role));
    saveStakeholders(updated);
  };

  const addStakeholder = () => {
    if (!newStakeholder.role.trim()) return;
    const updated = { ...stakeholders, [newStakeholder.role]: newStakeholder.responsibility };
    setStakeholders(updated);
    setNewStakeholder({ role: '', responsibility: '' });
    setShowAddStakeholder(false);
    saveStakeholders(updated);
  };

  const mergeStakeholders = () => {
    if (selectedStakeholders.length < 2) return;
    const primaryRole = selectedStakeholders[0];
    const mergedResponsibility = selectedStakeholders.map(role => stakeholders[role]).join('; ');
    const updated = { ...stakeholders };
    updated[primaryRole] = mergedResponsibility;
    selectedStakeholders.slice(1).forEach(role => delete updated[role]);
    setStakeholders(updated);
    setSelectedStakeholders([]);
    saveStakeholders(updated);
  };

  // Systems management functions
  const saveSystems = useCallback(async (newSystems: { [key: string]: any }) => {
    try {
      const projectId = currentProject?.id || synthesis?.project_id;
      if (!projectId) return;
      await api.put(`/api/analysis/systems/${projectId}`, { systems: newSystems });
      console.log('✅ Systems saved successfully');
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to save systems:', err);
      toast.error('Failed to save systems');
    }
  }, [currentProject?.id, synthesis, onRefresh]);

  const startEditSystem = (system: string) => {
    setEditingSystem(system);
    setEditSystemValue({ system, purpose: systems[system] });
  };

  const saveSystemEdit = () => {
    if (!editingSystem) return;
    const updated = { ...systems };
    if (editSystemValue.system !== editingSystem) {
      delete updated[editingSystem];
    }
    updated[editSystemValue.system] = editSystemValue.purpose;
    setSystems(updated);
    setEditingSystem(null);
    saveSystems(updated);
  };

  const deleteSystem = (system: string) => {
    const updated = { ...systems };
    delete updated[system];
    setSystems(updated);
    setSelectedSystems(selectedSystems.filter(s => s !== system));
    saveSystems(updated);
  };

  const addSystem = () => {
    if (!newSystem.system.trim()) return;
    const updated = { ...systems, [newSystem.system]: newSystem.purpose };
    setSystems(updated);
    setNewSystem({ system: '', purpose: '' });
    setShowAddSystem(false);
    saveSystems(updated);
  };

  const mergeSystems = () => {
    if (selectedSystems.length < 2) return;
    const primarySystem = selectedSystems[0];
    const mergedPurpose = selectedSystems.map(system => systems[system]).join('; ');
    const updated = { ...systems };
    updated[primarySystem] = mergedPurpose;
    selectedSystems.slice(1).forEach(system => delete updated[system]);
    setSystems(updated);
    setSelectedSystems([]);
    saveSystems(updated);
  };

  // Get all SOPs and filter by active tab
  const allSOPs = useMemo(() => analysisResults?.sops || [], [analysisResults?.sops]);
  const basicSOPs = useMemo(() => allSOPs.filter((s: any) => s.sop_type === 'basic'), [allSOPs]);
  const enhancedSOPs = useMemo(() => allSOPs.filter((s: any) => s.sop_type === 'enhanced'), [allSOPs]);
  const processMapSOPs = useMemo(() => allSOPs.filter((s: any) => s.sop_type === 'process_map'), [allSOPs]);

  // Get current SOP based on active tab
  const sop = useMemo(() => {
    let currentSOP = null;
    if (activeSOPTab === 'basic' && basicSOPs.length > 0) {
      currentSOP = basicSOPs[0];
    } else if (activeSOPTab === 'enhanced' && enhancedSOPs.length > 0) {
      currentSOP = enhancedSOPs[0];
    } else if (activeSOPTab === 'process_map' && processMapSOPs.length > 0) {
      currentSOP = processMapSOPs[0];
    }
    // Backward compatibility: fallback to old sop field
    return currentSOP || analysisResults?.sop;
  }, [activeSOPTab, basicSOPs, enhancedSOPs, processMapSOPs, analysisResults?.sop]);

  // Update sopRef when sop changes
  useEffect(() => {
    sopRef.current = sop;
  }, [sop]);

  useEffect(() => {
    if (sop?.content) {
      let sopData;
      try {
        sopData = typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content;
        const method = sopData.compliance_assessment?.processing_method;

        // Check if background processing is happening
        const isBackgroundProcessing = method && [
          'basic_immediate',
          'comprehensive_structured',
          'standard_analysis',
          'chunked_analysis'
        ].includes(method);

        setIsBackgroundProcessingDetected(isBackgroundProcessing);

        if (method === 'basic_immediate') {
          setSopType('basic');
        } else {
          setSopType('enhanced');
        }
      } catch {
        setSopType('basic');
      }
    }
  }, [sop]);

  // Fetch terminology definitions for SOP
  const fetchTerminologyDefinitions = useCallback(async () => {
    if (!currentProject?.id) return;

    setIsLoadingTerminology(true);
    try {
      console.log('🔍 Fetching terminology for project:', currentProject.id);
      const response = await api.get(`/api/terminology/${currentProject.id}/for-sop`);
      console.log('📥 Terminology API Response:', response.data);

      if (response.data.success) {
        const terms = response.data.data.terms || [];
        console.log('✅ Terminology terms loaded:', terms.length, terms);
        setTerminologyDefinitions(terms);
      } else {
        console.warn('⚠️ API returned success=false:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching terminology definitions:', error);
    } finally {
      setIsLoadingTerminology(false);
    }
  }, [currentProject?.id]);

  // Delete terminology definition from SOP
  const deleteTerminologyDefinition = useCallback(async (termId: number) => {
    if (!currentProject?.id) return;

    try {
      const response = await api.delete(`/api/terminology/${currentProject.id}/sop-definition/${termId}`);
      if (response.data.success) {
        toast.success('Definition removed from SOP');
        // Refresh terminology list
        fetchTerminologyDefinitions();
      }
    } catch (error) {
      console.error('Error deleting terminology definition:', error);
      toast.error('Failed to remove definition');
    }
  }, [currentProject?.id, fetchTerminologyDefinitions]);

  // Load terminology definitions when component mounts or project changes
  useEffect(() => {
    if (currentProject?.id && sop) {
      fetchTerminologyDefinitions();
    }
  }, [currentProject?.id, sop, fetchTerminologyDefinitions]);
  // REQ-SOP-2.2: Handle section editing
  const handleEditSection = useCallback((index: number, currentContent: string) => {
    setEditingSectionIndex(index);
    setEditedSections(prev => ({
      ...prev,
      [index]: {
        content: prev[index]?.content || currentContent,
        status: prev[index]?.status || 'ai_generated'
      }
    }));
  }, []);

  const handleSaveSection = useCallback(async (index: number) => {
    if (!currentProject || !sop) return;

    try {
      // Update section status to user_modified
      setEditedSections(prev => ({
        ...prev,
        [index]: { ...prev[index], status: 'user_modified' }
      }));

      // Get content and clean it before saving
      let contentToSave = editedSections[index].content;

      // Apply the same cleanup logic that's used for display to ensure consistency
      if (typeof contentToSave === 'string') {
        // Strip any HTML tags that might have been left over
        if (contentToSave.includes('<p>') || contentToSave.includes('<br>')) {
          contentToSave = contentToSave
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/p>\s*<p>/gi, '\n\n')
            .replace(/<p>/gi, '')
            .replace(/<\/p>/gi, '')
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&');
        }

        // Convert emoji-based markers to proper bracket format
        contentToSave = contentToSave
          .replace(/📋\*\*DOCUMENTED:\*\*/g, '[DOCUMENTED:')
          .replace(/📋DOCUMENTED:/g, '[DOCUMENTED:')
          .replace(/🔍\*\*INFERRED:\*\*/g, '[INFERRED:')
          .replace(/🔍INFERRED:/g, '[INFERRED:')
          .replace(/💭\*\*ASSUMED:\*\*/g, '[ASSUMED:')
          .replace(/💭ASSUMED:/g, '[ASSUMED:')
          .replace(/🔍\*\*CONTROL POINT:\*\*/g, '[CONTROL POINT:')
          .replace(/🔍CONTROL POINT:/g, '[CONTROL POINT:')
          .replace(/⚠️\*\*GAP IDENTIFIED:\*\*/g, '[GAP IDENTIFIED:')
          .replace(/⚠️GAP IDENTIFIED:/g, '[GAP IDENTIFIED:')
          .replace(/📸\*\*SCREENSHOT NEEDED:\*\*/g, '[SCREENSHOT NEEDED:')
          .replace(/📸SCREENSHOT NEEDED:/g, '[SCREENSHOT NEEDED:')
          .replace(/📊\*\*FLOWCHART NEEDED:\*\*/g, '[FLOWCHART NEEDED:')
          .replace(/📊FLOWCHART NEEDED:/g, '[FLOWCHART NEEDED:')
          .replace(/⚡\*\*CONFLICT:\*\*/g, '[CONFLICT:')
          .replace(/⚡CONFLICT:/g, '[CONFLICT:')
          .replace(/📄\*\*SOURCE:\*\*/g, '[SOURCE:')
          .replace(/📄SOURCE:/g, '[SOURCE:')
          .replace(/💡\*\*ASSUMPTION:\*\*/g, '[ASSUMPTION:')
          .replace(/💡ASSUMPTION:/g, '[ASSUMPTION:');

        // Remove bold formatting from marker labels: [**DOCUMENTED:**text] -> [DOCUMENTED:text]
        contentToSave = contentToSave
          .replace(/\[\*\*DOCUMENTED:\*\*/g, '[DOCUMENTED:')
          .replace(/\[\*\*INFERRED:\*\*/g, '[INFERRED:')
          .replace(/\[\*\*ASSUMED:\*\*/g, '[ASSUMED:')
          .replace(/\[\*\*CONTROL POINT:\*\*/g, '[CONTROL POINT:')
          .replace(/\[\*\*GAP IDENTIFIED:\*\*/g, '[GAP IDENTIFIED:')
          .replace(/\[\*\*SCREENSHOT NEEDED:\*\*/g, '[SCREENSHOT NEEDED:')
          .replace(/\[\*\*FLOWCHART NEEDED:\*\*/g, '[FLOWCHART NEEDED:')
          .replace(/\[\*\*CONFLICT:\*\*/g, '[CONFLICT:')
          .replace(/\[\*\*SOURCE:\*\*/g, '[SOURCE:')
          .replace(/\[\*\*ASSUMPTION:\*\*/g, '[ASSUMPTION:');

        // Add closing brackets if missing
        contentToSave = contentToSave.replace(
          /(\[(?:DOCUMENTED|INFERRED|ASSUMED|CONTROL POINT|GAP IDENTIFIED|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION):)([^\]]+?)(?=(?:\.\s|\n|$|\s+\[(?:DOCUMENTED|INFERRED|ASSUMED|CONTROL POINT|GAP IDENTIFIED|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION):))/g,
          '$1$2]'
        );
      }

      // Save to backend with cleaned content
      const sopData = typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content;
      sopData.sections[index].content = contentToSave;
      sopData.sections[index].modification_status = 'user_modified';
      sopData.sections[index].last_edited_at = new Date().toISOString();

      await api.put(`/api/analysis/sops/${sop.id}`, {
        content: JSON.stringify(sopData)
      });

      toastService.success('Section saved successfully');
      setEditingSectionIndex(null);

      // Clear the edited section from state after successful save
      setEditedSections(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });

      await loadAnalysisResults();
    } catch (error: any) {
      console.error('Save section error:', error);
      toastService.error('Failed to save section');
    }
  }, [currentProject, sop, editedSections, loadAnalysisResults]);

  const handleCancelEdit = useCallback((index: number) => {
    setEditingSectionIndex(null);
    // Revert to original content
    setEditedSections(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  }, []);

  const handleContentChange = useCallback((index: number, newContent: string) => {
    setEditedSections(prev => ({
      ...prev,
      [index]: { ...prev[index], content: newContent }
    }));

    // Auto-save after 30 seconds
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    const timer = setTimeout(() => {
      handleSaveSection(index);
    }, 30000); // 30 seconds

    setAutoSaveTimer(timer);
  }, [autoSaveTimer, handleSaveSection]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [autoSaveTimer]);

  // Auto-generate Key Definitions section from approved terminology
  const handleAutoGenerateKeyDefinitions = useCallback(async () => {
    if (!currentProject || !sop || terminologyDefinitions.length === 0) {
      toast.error('No approved terminology available to generate Key Definitions');
      return;
    }

    try {
      // Generate content from terminology definitions with green highlighting
      let keyDefinitionsContent = '<h3>Key Definitions</h3>\n<dl>\n';
      terminologyDefinitions.forEach((term: any) => {
        keyDefinitionsContent += `  <dt><strong style="color: #16a34a; font-weight: 600;">${term.term}</strong></dt>\n`;
        keyDefinitionsContent += `  <dd style="margin-bottom: 12px;">${term.definition}</dd>\n`;
      });
      keyDefinitionsContent += '</dl>';

      // Check if Key Definitions section already exists
      const sopData = typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content;
      const existingKeyDefIndex = sopData.sections.findIndex(
        (s: any) => s.section_title === 'Key Definitions' || s.title === 'Key Definitions'
      );

      if (existingKeyDefIndex !== -1) {
        // Update existing section
        sopData.sections[existingKeyDefIndex].content = keyDefinitionsContent;
        sopData.sections[existingKeyDefIndex].modification_status = 'user_modified';
        sopData.sections[existingKeyDefIndex].last_edited_at = new Date().toISOString();

        await api.put(`/api/analysis/sops/${sop.id}`, {
          content: JSON.stringify(sopData)
        });

        toast.success(`Key Definitions section updated with ${terminologyDefinitions.length} terms`);
        await loadAnalysisResults();
      } else {
        // Create new section - insert at position 2 (index 1)
        const insertIndex = 1; // Position 2
        const customSection = {
          section_number: '2.0',
          section_title: 'Key Definitions',
          content: keyDefinitionsContent,
          modification_status: 'user_modified',
          is_custom: true,
          created_at: new Date().toISOString()
        };

        // Insert at position 2
        sopData.sections.splice(insertIndex, 0, customSection);

        // Renumber subsequent sections
        for (let i = insertIndex + 1; i < sopData.sections.length; i++) {
          sopData.sections[i].section_number = `${i + 1}.0`;
        }

        await api.put(`/api/analysis/sops/${sop.id}`, {
          content: JSON.stringify(sopData)
        });

        toast.success(`Key Definitions section created with ${terminologyDefinitions.length} terms`);
        await loadAnalysisResults();
      }
    } catch (error: any) {
      console.error('Error generating Key Definitions:', error);
      toast.error(error.response?.data?.message || 'Failed to generate Key Definitions section');
    }
  }, [currentProject, sop, terminologyDefinitions, loadAnalysisResults]);

  // REQ-SOP-2.3: Handle custom section addition
  const handleAddSection = useCallback(async (newSection: { title: string; content: string; template?: string }) => {
    if (!currentProject || !sop) return;

    try {
      const sopData = typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content;

      // Determine insert position
      const insertIndex = insertAfterIndex !== null ? insertAfterIndex + 1 : sopData.sections.length;

      // Create new section with auto-numbered section_number
      const newSectionNumber = `${insertIndex + 1}.0`;
      const customSection = {
        section_number: newSectionNumber,
        section_title: newSection.title,
        content: newSection.content,
        modification_status: 'user_modified',
        is_custom: true,
        created_at: new Date().toISOString()
      };

      // Insert section at position
      sopData.sections.splice(insertIndex, 0, customSection);

      // Renumber subsequent sections
      for (let i = insertIndex + 1; i < sopData.sections.length; i++) {
        const oldNumber = sopData.sections[i].section_number;
        const newNumber = `${i + 1}.0`;
        sopData.sections[i].section_number = newNumber;

        // Update any subsection numbers if they exist
        if (oldNumber && oldNumber.includes('.')) {
          const parts = oldNumber.split('.');
          if (parts.length > 1) {
            sopData.sections[i].section_number = `${i + 1}.${parts.slice(1).join('.')}`;
          }
        }
      }

      // Save to backend
      await api.post(`/api/analysis/sops/${sop.id}/sections`, {
        content: JSON.stringify(sopData)
      });

      toastService.success('Custom section added successfully');
      await loadAnalysisResults();

      setIsAddSectionModalOpen(false);
      setInsertAfterIndex(null);
    } catch (error: any) {
      console.error('Add section error:', error);
      toastService.error('Failed to add section');
    }
  }, [currentProject, sop, insertAfterIndex, loadAnalysisResults]);

  const openAddSectionModal = useCallback((afterIndex: number | null = null) => {
    setInsertAfterIndex(afterIndex);
    setIsAddSectionModalOpen(true);
  }, []);

  const generateBasicSOP = useCallback(async () => {
    if (!currentProject) {
      toastService.error('Please select a project first');
      return;
    }
    setIsBasicSOPGenerating(true);
    try {
      toastService.info('🚀 Starting Basic SOP generation...');
      const response = await api.post(`/api/analysis/generate-basic-sop/${currentProject.id}`);

      if (response.data.sop_id) {
        toastService.success('✅ Basic SOP generated successfully!');
        await loadAnalysisResults();
      }
    } catch (error: any) {
      console.error('Basic SOP generation error:', error);
      toastService.error(error.response?.data?.error || 'Failed to generate Basic SOP');
    } finally {
      setIsBasicSOPGenerating(false);
    }
  }, [currentProject, loadAnalysisResults]);

  const generateEnhancedSOP = useCallback(async () => {
    if (!currentProject) {
      toast.error('Please select a project first');
      return;
    }
    setIsEnhancingInProgress(true);
    setIsStatusMessageDismissed(false); // Reset dismissed state for new generation
    // Clear any existing intervals
    if (sopPollIntervalRef.current) {
      clearInterval(sopPollIntervalRef.current);
      sopPollIntervalRef.current = null;
    }

    try {
      // Start the Enhanced SOP generation
      const response = await api.post(`/api/analysis/generate-enhanced-sop/${currentProject.id}`);
      console.log('✅ API Response:', response.data);

      // Set background processing state instead of upgrading state
      setIsBackgroundProcessingDetected(true);

      // Show initial toast
      toast.success('🚀 Enhanced SOP generation started! Processing in background...', {
        duration: 4000
      });

      // Start simple background polling
      let pollCount = 0;
      const maxPolls = 120; // 10 minutes at 5-second intervals

      sopPollIntervalRef.current = setInterval(async () => {
        pollCount++;
        console.log(`🔍 Background polling ${pollCount}/${maxPolls}`);

        try {
          const resultsResponse = await api.get(`/api/analysis/results/${currentProject.id}`);
          const latestSop = resultsResponse.data.sop;

          if (latestSop && latestSop.content) {
            // Check if this is a newer SOP
            const isNewOrUpdated = !sopRef.current ||
              latestSop.id !== sopRef.current.id ||
              latestSop.updated_at !== sopRef.current.updated_at ||
              latestSop.version > (sopRef.current.version || 1);

            console.log('📊 Background check:', {
              isNewOrUpdated,
              latestVersion: latestSop.version,
              currentVersion: sopRef.current?.version,
              latestUpdated: latestSop.updated_at,
              currentUpdated: sopRef.current?.updated_at
            });

            if (isNewOrUpdated) {
              let isEnhanced = false;

              try {
                const sopData = typeof latestSop.content === 'string' ?
                  JSON.parse(latestSop.content) : latestSop.content;

                // Check for enhanced indicators
                const hasMultipleSections = sopData.sections && sopData.sections.length >= 4;
                const hasSubstantialContent = JSON.stringify(sopData).length > 3000;
                const hasEnhancedMarkers = JSON.stringify(sopData).includes('CONTROL POINT') ||
                  JSON.stringify(sopData).includes('GAP IDENTIFIED');

                isEnhanced = hasMultipleSections && (hasSubstantialContent || hasEnhancedMarkers);

                console.log('📋 Enhancement check:', {
                  hasMultipleSections,
                  hasSubstantialContent,
                  hasEnhancedMarkers,
                  isEnhanced
                });

              } catch (parseError) {
                // If parsing fails but we have substantial content, consider it enhanced
                const hasContent = typeof latestSop.content === 'string' ?
                  latestSop.content.length > 3000 :
                  JSON.stringify(latestSop.content).length > 3000;
                isEnhanced = hasContent;
              }

              if (isEnhanced) {
                console.log('🎉 Enhanced SOP completed in background!');

                // Clear polling
                if (sopPollIntervalRef.current) {
                  clearInterval(sopPollIntervalRef.current);
                  sopPollIntervalRef.current = null;
                }

                setIsEnhancingInProgress(false);

                // Hide background processing indicator
                setIsBackgroundProcessingDetected(false);

                // Show success message and reload
                toast.success('🎉 Enhanced SOP generation completed! Refreshing content...');
                await loadAnalysisResults();
                return;
              }
            }
          }

          // Stop polling after max attempts
          if (pollCount >= maxPolls) {
            console.log('⏱️ Background polling timeout');

            if (sopPollIntervalRef.current) {
              clearInterval(sopPollIntervalRef.current);
              sopPollIntervalRef.current = null;
            }

            setIsEnhancingInProgress(false);
            setIsBackgroundProcessingDetected(false);
            toast('⏱️ Enhanced SOP generation is taking longer than expected. It may still be processing.', {
              duration: 6000
            });
          }

        } catch (pollError) {
          console.error('❌ Background polling error:', pollError);
        }
      }, 5000); // Poll every 5 seconds

    } catch (error) {
      console.error('❌ Enhanced SOP generation failed:', error);
      setIsEnhancingInProgress(false);
      setIsBackgroundProcessingDetected(false);
      toast.error('Enhanced SOP generation failed. Please try again.');
    }
  }, [currentProject, loadAnalysisResults]);

  // Fetch process map ID on component mount
  useEffect(() => {
    const fetchProcessMapId = async () => {
      if (!currentProject?.id) return;

      try {
        const response = await api.get(`/api/analysis/projects/${currentProject.id}/process-maps`);
        if (response.data?.process_maps && response.data.process_maps.length > 0) {
          setProcessMapId(response.data.process_maps[0].id);
        }
      } catch (error) {
        console.error('Error fetching process map ID:', error);
      }
    };

    fetchProcessMapId();
  }, [currentProject?.id]);

  // Regenerate SOP from Process Map (no AI)
  const regenerateFromProcessMap = useCallback(async () => {
    if (!processMapId) {
      toastService.error('No process map found for this project');
      return;
    }

    setIsRegeneratingFromMap(true);
    try {
      toastService.info('🗺️ Regenerating SOP from process map...');

      const response = await api.post('/api/analysis/sops/regenerate-from-map', {
        processMapId: processMapId,
        preserveVerified: true // Default: preserve human-verified content
      });

      if (response.data.success) {
        const { sop, mappings, message, preservation_stats } = response.data;

        // Show detailed success message with preservation stats
        if (preservation_stats && preservation_stats.preservation_enabled) {
          toastService.success(
            `✅ ${message}\n` +
            `🔒 Preserved: ${preservation_stats.preserved_steps} steps\n` +
            `🔄 Regenerated: ${preservation_stats.regenerated_steps} steps`
          );
        } else {
          toastService.success(`✅ ${message}`);
        }

        console.log('📊 Regeneration result:', {
          sopId: sop.id,
          mappingsCreated: mappings.created,
          preservationStats: preservation_stats
        });

        // Reload to show updated SOP
        await loadAnalysisResults();
      }
    } catch (error: any) {
      console.error('Regenerate from process map error:', error);
      toastService.error(error.response?.data?.error || 'Failed to regenerate SOP from process map');
    } finally {
      setIsRegeneratingFromMap(false);
    }
  }, [processMapId, loadAnalysisResults]);

  useEffect(() => {
    return () => {
      if (sopPollIntervalRef.current) {
        clearInterval(sopPollIntervalRef.current);
        sopPollIntervalRef.current = null;
      }
      // Reset button state on component unmount
      setIsEnhancingInProgress(false);
    };
  }, []);


  useEffect(() => {
    if (synthesis?.sections && Array.isArray(synthesis.sections) && synthesis.sections.length > 0) {
      console.log('📥 ProcessMaps: Loading sections from backend:', synthesis.sections.length, 'sections');
      setSections(synthesis.sections);
    }

    // Load sections map from backend
    if (synthesis?.sections_map) {
      const newSectionsMap = new Map();
      Object.entries(synthesis.sections_map).forEach(([key, value]) => {
        newSectionsMap.set(key, value as string);
      });
      setSectionsMap(newSectionsMap);
    }

    // Load section step order from backend
    if (synthesis?.section_step_order) {
      console.log('📥 ProcessMaps: Loading section step order from backend');
      const orderMap = new Map();
      Object.entries(synthesis.section_step_order).forEach(([sectionId, order]) => {
        if (Array.isArray(order)) {
          orderMap.set(sectionId, order);
        }
      });
      setSectionStepOrder(orderMap);
      console.log('📥 ProcessMaps: Loaded section step order from backend:', Object.fromEntries(orderMap));
    }
  }, [
    synthesis?.sections,
    synthesis?.sections_map,
    synthesis?.section_step_order,
    JSON.stringify(synthesis?.sections),
    JSON.stringify(synthesis?.sections_map),
    JSON.stringify(synthesis?.section_step_order)
  ]);

  // ADD STEPS LOADING useEffect
  useEffect(() => {
    console.log('📥 ProcessMaps: Loading steps from Enhanced Process Synthesis Tab...', {
      'synthesis.process_steps exists': !!synthesis?.process_steps,
      'synthesis.process_steps length': synthesis?.process_steps?.length || 0
    });

    let stepsArr: any[] = [];

    // PRIORITY 1: Use process_steps from backend (Enhanced Tab data)
    if (synthesis?.process_steps && Array.isArray(synthesis.process_steps) && synthesis.process_steps.length > 0) {
      console.log('📥 ProcessMaps: Loading from synthesis.process_steps (Enhanced Tab Updates):', synthesis.process_steps.length, 'steps');
      stepsArr = synthesis.process_steps;
    }
    // PRIORITY 2: Fallback to synthesis_text
    else if (synthesis?.synthesis_text) {
      console.log('📥 ProcessMaps: Falling back to synthesis_text parsing...');
      let synthesisData;
      try {
        synthesisData = typeof synthesis.synthesis_text === 'string'
          ? JSON.parse(synthesis.synthesis_text)
          : synthesis.synthesis_text;
      } catch (error) {
        console.error('ProcessMaps: Failed to parse synthesis_text:', error);
        synthesisData = synthesis.synthesis_text;
      }

      if (synthesisData?.end_to_end_process && Array.isArray(synthesisData.end_to_end_process)) {
        console.log('📥 ProcessMaps: Using end_to_end_process fallback:', synthesisData.end_to_end_process.length, 'steps');
        stepsArr = synthesisData.end_to_end_process;
      }
    }

    if (stepsArr.length > 0) {
      console.log('✅ ProcessMaps: Setting steps from Enhanced Tab sync:', stepsArr.length, 'steps');
      setSteps(stepsArr);
    } else {
      console.log('❌ ProcessMaps: No steps found - clearing steps array');
      setSteps([]);
    }
  }, [
    synthesis?.process_steps,
    synthesis?.synthesis_text,
    synthesis?.id,
    synthesis?.updated_at,
    JSON.stringify(synthesis?.process_steps)
  ]);

  // Background processing polling effect
  useEffect(() => {
    if (isBackgroundProcessingDetected && currentProject && !isUpgrading) {
      let pollAttempts = 0;
      const maxPollAttempts = 120; // 10 minutes at 5-second intervals

      toast('🔄 Background Enhanced SOP generation detected! Monitoring for completion...', {
        icon: 'ℹ️',
        duration: 5000
      });

      backgroundPollingRef.current = setInterval(async () => {
        pollAttempts++;

        try {
          const resultsResponse = await api.get(`/api/analysis/results/${currentProject.id}`);
          const currentSop = resultsResponse.data.sop;

          if (currentSop && currentSop.content) {
            let sopData;
            try {
              sopData = typeof currentSop.content === 'string' ? JSON.parse(currentSop.content) : currentSop.content;
              const method = sopData.compliance_assessment?.processing_method;

              // Check if enhanced processing is complete
              const isComplete = method && [
                'comprehensive_enhanced',
                'hybrid_enhanced',
                'single_prompt_enhanced'
              ].includes(method);

              if (isComplete) {
                // Enhanced SOP is ready!
                if (backgroundPollingRef.current) {
                  clearInterval(backgroundPollingRef.current);
                  backgroundPollingRef.current = null;
                }

                setIsBackgroundProcessingDetected(false);
                toast.success('🎉 Enhanced SOP generation completed! Refreshing results...');
                await loadAnalysisResults();
                return;
              }
            } catch (e) {
              console.warn('Error parsing SOP during background polling:', e);
            }
          }

        } catch (pollError) {
          console.warn('Background polling error (will continue):', pollError);
        }

        // Stop polling after max attempts
        if (pollAttempts >= maxPollAttempts) {
          if (backgroundPollingRef.current) {
            clearInterval(backgroundPollingRef.current);
            backgroundPollingRef.current = null;
          }
          setIsBackgroundProcessingDetected(false);
          toast('⚠️ Background processing monitoring stopped after 10 minutes. Please refresh manually to check for updates.', {
            icon: '⚠️',
            duration: 8000
          });
        }
      }, 5000);
    }

    return () => {
      if (backgroundPollingRef.current) {
        clearInterval(backgroundPollingRef.current);
        backgroundPollingRef.current = null;
      }
    };
  }, [isBackgroundProcessingDetected, currentProject, isUpgrading, loadAnalysisResults]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (sopProgress) {
        setSopProgress(null);
      }
      // Clear any running intervals
      if (sopPollIntervalRef.current) {
        clearInterval(sopPollIntervalRef.current);
        sopPollIntervalRef.current = null;
      }
      if (backgroundPollingRef.current) {
        clearInterval(backgroundPollingRef.current);
        backgroundPollingRef.current = null;
      }
    };
  }, [sopProgress]);

  // Show a loader during the main analysis process
  // const analysisHasRun = analysisProgress && (analysisProgress.completed > 0 || analysisProgress.failed > 0);
  // const isWaitingForSopAfterAnalysis = !isAnalyzing && analysisHasRun && !sop;
  // const isAnalysisInProgress = isAnalyzing || (analysisProgress && analysisProgress.analyzing > 0);

  // const shouldShowLoader = isAnalysisInProgress || isWaitingForSopAfterAnalysis || (analysisProgress && analysisProgress.total > 0 && !sop);


  
  // if (shouldShowLoader) {
  //   let message = 'Loading SOP...';
  //   let subtitle = '';

  //   if (isAnalyzing || isAnalysisInProgress) {
  //     message = 'Analysis in Progress';
  //     subtitle = 'The SOP will be generated automatically upon completion.';
  //   } else if (isWaitingForSopAfterAnalysis) {
  //     message = 'Generating SOP';
  //     subtitle = 'Finalizing analysis results and creating your Standard Operating Procedure...';
  //   } else if (analysisProgress && analysisProgress.total > 0) {
  //     message = 'Processing Documents';
  //     subtitle = `${analysisProgress.completed}/${analysisProgress.total} documents processed. SOP generation will begin shortly.`;
  //   }

  //   return (
  //     <div className="flex flex-col items-center justify-center py-16">

  //       {isBackgroundProcessingDetected && !isUpgrading && (
  //         <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
  //           <div className="flex items-center justify-between">
  //             <div className="flex items-center space-x-2">
  //               <div className="flex space-x-1">
  //                 <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
  //                 <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
  //                 <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
  //               </div>
  //               <span className="text-blue-800 font-medium">Enhanced SOP generation in progress...</span>
  //             </div>
  //             <span className="text-xs text-blue-600">This may take 3-8 minutes</span>
  //           </div>
  //         </div>
  //       )}
  //       {/* Enhanced Loading Animation */}
  //       <div className="relative mb-6">
  //         <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
  //         <div className="animate-spin rounded-full h-16 w-16 border-4 border-electric-blue border-t-transparent absolute top-0 left-0"></div>
  //         <div className="absolute inset-0 flex items-center justify-center">
  //           <div className="w-6 h-6 bg-electric-blue rounded-full animate-pulse"></div>
  //         </div>
  //       </div>

  //       <h3 className="text-xl font-semibold text-gray-800 mb-2">{message}</h3>
  //       <p className="text-gray-600 text-center max-w-md">{subtitle}</p>

  //       {/* Progress indicator if analysis progress is available */}
  //       {analysisProgress && analysisProgress.total > 0 && (
  //         <div className="w-64 mt-6">
  //           <div className="bg-gray-200 rounded-full h-2">
  //             <div
  //               className="bg-electric-blue h-2 rounded-full transition-all duration-300"
  //               style={{ width: `${(analysisProgress.completed / analysisProgress.total) * 100}%` }}
  //             ></div>
  //           </div>
  //           <div className="flex justify-between text-xs text-gray-500 mt-1">
  //             <span>Processing...</span>
  //             <span>{Math.round((analysisProgress.completed / analysisProgress.total) * 100)}%</span>
  //           </div>
  //         </div>
  //       )}

  //       {/* Helpful tip */}
  //       <div className="mt-8 p-4 bg-blue-50 rounded-lg max-w-md text-center">
  //         <p className="text-sm text-blue-700">
  //           💡 <strong>Tip:</strong> This process typically takes 2-5 minutes depending on the number and complexity of your documents.
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  if (isAnalyzing || (analysisProgress && analysisProgress.analyzing > 0)) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="relative mb-6">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-electric-blue border-t-transparent absolute top-0 left-0"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-6 h-6 bg-electric-blue rounded-full animate-pulse"></div>
        </div>
      </div>

      <h3 className="text-xl font-semibold text-gray-800 mb-2">Processing Documents</h3>
      <p className="text-gray-600 text-center max-w-md">
        {analysisProgress ? 
          `${analysisProgress.completed}/${analysisProgress.total} documents processed. SOP generation will begin shortly.` :
          'The SOP will be generated automatically upon completion.'
        }
      </p>

      {/* Progress indicator if analysis progress is available */}
      {analysisProgress && analysisProgress.total > 0 && (
        <div className="w-64 mt-6">
          <div className="bg-gray-200 rounded-full h-2">
            <div
              className="bg-electric-blue h-2 rounded-full transition-all duration-300"
              style={{ width: `${(analysisProgress.completed / analysisProgress.total) * 100}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Processing...</span>
            <span>{Math.round((analysisProgress.completed / analysisProgress.total) * 100)}%</span>
          </div>
        </div>
      )}

      <div className="mt-8 p-4 bg-blue-50 rounded-lg max-w-md text-center">
        <p className="text-sm text-blue-700">
          💡 <strong>Tip:</strong> This process typically takes 2-5 minutes depending on the number and complexity of your documents.
        </p>
      </div>
    </div>
  );
}

  if (isUpgrading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 max-w-2xl mx-auto">
        {/* Enhanced Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-6 shadow-inner">
          <div
            className="bg-gradient-to-r from-electric-blue to-purple-600 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
            style={{ width: `${sopProgress?.progress || 0}%` }}
          ></div>
        </div>

        {/* Main Loading Animation */}
        <div className="relative mb-6">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-electric-blue border-t-transparent absolute top-0 left-0"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 bg-electric-blue rounded-full animate-pulse"></div>
          </div>
        </div>

        {/* Stage Message */}
        <div className="text-center mb-4">
          <p className="text-gray-700 text-lg font-medium mb-2">
            {sopProgress?.stage || 'Upgrading to Enhanced SOP...'}
          </p>
          <div className="flex items-center justify-center space-x-2 text-electric-blue">
            <div className="w-2 h-2 bg-electric-blue rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-electric-blue rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-electric-blue rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>

        {/* Progress Details */}
        <div className="text-sm text-gray-600 text-center bg-gray-50 rounded-lg p-4 w-full">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            <div>
              <span className="font-semibold text-electric-blue">{sopProgress?.progress || 0}%</span>
              <p className="text-xs text-gray-500">Complete</p>
            </div>
            {sopProgress?.startTime && (
              <div>
                <span className="font-semibold text-green-600">
                  {Math.floor((Date.now() - sopProgress.startTime) / 60000)}:{String(Math.floor(((Date.now() - sopProgress.startTime) % 60000) / 1000)).padStart(2, '0')}
                </span>
                <p className="text-xs text-gray-500">Elapsed</p>
              </div>
            )}
            {sopProgress?.estimatedTime && sopProgress.estimatedTime > 0 && (
              <div>
                <span className="font-semibold text-orange-600">
                  ~{Math.ceil(sopProgress.estimatedTime / 60)} min
                </span>
                <p className="text-xs text-gray-500">Remaining</p>
              </div>
            )}
          </div>

          <div className="text-xs text-gray-500 bg-blue-50 rounded p-3 border-l-4 border-blue-300">
            <p className="mb-1">🔬 <strong>What's happening:</strong> Our AI is analyzing your documents and creating comprehensive enterprise-grade SOP documentation</p>
            <p className="mb-1">📊 <strong>Expected output:</strong> 15k-25k word detailed procedural documentation with controls, risks, and compliance mapping</p>
            <p>⏱️ <strong>Normal duration:</strong> 3-8 minutes depending on document complexity and system load</p>
          </div>
        </div>
      </div>
    );
  }

  if (!sop) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>

        <h3 className="text-xl font-semibold text-gray-600 mb-2">No SOP Available</h3>
        <p className="text-gray-500 text-center max-w-md mb-6">
          Run the full analysis or generate an SOP to begin creating your Standard Operating Procedure.
        </p>

        {/* Action buttons */}
        <div className="space-y-3 flex flex-col items-center">
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-electric-blue text-white rounded-lg hover:bg-blue-400 transition-colors"
          >
            🔄 Refresh Page
          </button>
          <p className="text-xs text-gray-400 text-center">
            If analysis was recently completed, try refreshing the page
          </p>
        </div>
      </div>
    );
  }

  let sopData;
  try {
    sopData = typeof sop.content === 'string' ? JSON.parse(sop.content) : sop.content;
  } catch (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <p>Error parsing SOP content. It might be malformed JSON.</p>
        <pre className="mt-2 text-sm bg-red-100 p-2 rounded whitespace-pre-wrap">
          {sop.content}
        </pre>
      </div>
    );
  }

  // Additional safety check for sopData
  if (!sopData) {
    return (
      <div className="p-4 bg-yellow-50 text-yellow-700 rounded-lg">
        <p>SOP data is not available. Please try refreshing the page or re-generating the SOP.</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
        >
          🔄 Refresh Page
        </button>
      </div>
    );
  }

  const processingMethod = sopData?.compliance_assessment?.processing_method;
  let sopStatusMessage = '';
  let sopStatusClass = '';

  // Determine if background processing is happening
  const isBackgroundProcessing = processingMethod && [
    'basic_immediate',
    'comprehensive_structured',
    'standard_analysis',
    'chunked_analysis'
  ].includes(processingMethod);

  const isEnhancedComplete = processingMethod && [
    'comprehensive_enhanced',
    'hybrid_enhanced',
    'single_prompt_enhanced'
  ].includes(processingMethod);

  if (isUpgrading) {
    sopStatusMessage = '';
  } else if (isBackgroundProcessing) {
    sopStatusMessage = '🔄 Enhanced SOP is being generated in the background. Displaying current version...';
    sopStatusClass = 'bg-yellow-100 border border-yellow-300 text-yellow-800';
  } else if (isEnhancedComplete) {
    sopStatusMessage = '✅ Comprehensive Enhanced SOP is ready!';
    sopStatusClass = 'bg-green-100 border border-green-300 text-green-800';
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-super-dark-purple">
        {sopType === 'enhanced' ? 'Comprehensive Enhanced SOP' : 'Standard Operating Procedure'}
      </h2>

      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          {sopType === 'basic' && (
            <button
              onClick={generateEnhancedSOP}
              disabled={isEnhancingInProgress}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {isEnhancingInProgress ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Enhancing...</span>
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  <span>Upgrade to Enhanced SOP</span>
                </>
              )}
            </button>
          )}

          {/* Regenerate Buttons */}
          <div className="flex items-center space-x-3">
            {/* Basic SOP Button */}
            <button
              onClick={generateBasicSOP}
              disabled={isBasicSOPGenerating || isEnhancingInProgress}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              title="Generate Basic SOP (faster, simpler)"
            >
              {isBasicSOPGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  <span>Generate Basic SOP</span>
                </>
              )}
            </button>

            {/* Enhanced SOP Button */}
            {sopType === 'enhanced' && (
              <button
                onClick={generateEnhancedSOP}
                disabled={isEnhancingInProgress || isBasicSOPGenerating}
                className="bg-electric-blue text-white px-4 py-2 rounded-lg hover:bg-darker-blue transition-colors disabled:opacity-50 flex items-center space-x-2"
                title="Generate Enhanced SOP (detailed, comprehensive)"
              >
                {isEnhancingInProgress ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    <span>Generate Enhanced SOP</span>
                  </>
                )}
              </button>
            )}

            {/* Smart RACI Button - Generate or View based on data existence */}
            <button
              onClick={() => window.location.href = hasRACIData
                ? `/app/raci-matrix?projectId=${currentProject?.id}`
                : `/app/raci-matrix?projectId=${currentProject?.id}&autoGenerate=true`
              }
              className={`${hasRACIData ? 'bg-blue-500 hover:bg-blue-600' : 'bg-cyan-500 hover:bg-cyan-600'} text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2`}
              title={hasRACIData
                ? "View RACI Matrix (auto-syncs with latest stakeholders and process map steps)"
                : "Generate RACI Matrix with AI (Responsible, Accountable, Consulted, Informed)"
              }
            >
              <Users className="w-4 h-4" />
              <span>{hasRACIData ? 'View RACI Data' : 'Generate RACI'}</span>
            </button>

            {/* Regenerate from Process Map Button - Hidden for now */}
            {/* {processMapId && (
              <button
                onClick={regenerateFromProcessMap}
                disabled={isRegeneratingFromMap || isBasicSOPGenerating || isEnhancingInProgress}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                title="Regenerate SOP directly from Process Map (fast, no AI)"
              >
                {isRegeneratingFromMap ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Regenerating...</span>
                  </>
                ) : (
                  <>
                    <Merge className="w-4 h-4" />
                    <span>Regenerate from Process Map</span>
                  </>
                )}
              </button>
            )} */}
          </div>
        </div>

        {/* Export Button on the right side */}
        <div>
          <button
            onClick={async () => {
              if (!currentProject || isExportingSOP) return;

              setIsExportingSOP(true);
              const toastId = 'export-sop';
              try {
                toast.loading('Preparing SOP document...', { id: toastId, duration: Infinity });

                const response = await api.post(`/api/export/sop/${currentProject.id}`);
                const downloadUrl = response.data.download_url;

                toast.loading('Downloading SOP file...', { id: toastId, duration: Infinity });

                // Use api instance which handles authentication properly via cookies
                const fileResponse = await api.get(downloadUrl, {
                  responseType: 'blob'
                });

                const blob = new Blob([fileResponse.data], {
                  type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${currentProject.name}_SOP.docx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                toast.success('SOP exported successfully!', { id: toastId });
              } catch (error) {
                console.error('Export SOP error:', error);
                toast.error('Failed to export SOP. Please try again.', { id: toastId });
              } finally {
                setIsExportingSOP(false);
              }
            }}
            disabled={!analysisResults?.sop || isExportingSOP}
            className="bg-electric-blue text-white px-4 py-2 rounded-lg hover:bg-darker-blue transition-colors disabled:opacity-50 flex items-center space-x-2"
            title="Export SOP to Word document"
          >
            {isExportingSOP ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span>Export SOP</span>
              </>
            )}
          </button>
        </div>
      </div>


      {sopStatusMessage && !isStatusMessageDismissed && (
        <div className={`${sopStatusClass} rounded p-3 mb-4 ${isBackgroundProcessingDetected ? 'animate-pulse' : ''}`}>
          <div className="flex items-center justify-between">
            <span>{sopStatusMessage}</span>
            <div className="flex items-center space-x-2">
              {isBackgroundProcessingDetected && (
                <>
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <span className="text-xs text-gray-600 ml-2">Auto-refreshing...</span>
                </>
              )}
              <button
                onClick={() => setIsStatusMessageDismissed(true)}
                className="ml-2 p-1 hover:bg-black/10 rounded transition-colors"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No SOP for selected tab */}
      {!sop && allSOPs.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center mb-6">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-amber-900 mb-2">
            No {activeSOPTab === 'basic' ? 'Basic' : activeSOPTab === 'enhanced' ? 'Enhanced' : 'Process Map'} SOP Found
          </h3>
          <p className="text-amber-700 mb-4">
            This type of SOP hasn't been generated yet. Try generating it using the buttons above.
          </p>
        </div>
      )}

      {/* Sync Status Warning - Show in all SOP tabs */}
      {sop && currentProject && processMapId && (
        <div className="mb-4">
          <OutOfSyncWarning
            projectId={currentProject.id}
            showIfSynced={true}
            onRegenerateSuccess={loadAnalysisResults}
          />
        </div>
      )}

      {/* SOP Header */}
      {sop && (
      <>
      <div className="bg-white border overflow-hidden rounded-lg border-l-4 border-green-500">
        <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-white border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-md font-semibold mb-2">{sopData?.title || sop?.title || 'Standard Operating Procedure'}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Version:</span>
              <p>{sopData?.version || sop?.version || '1.0'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Effective Date:</span>
              <p>{sopData?.effective_date || new Date().toLocaleDateString()}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Compliance Score:</span>
              <p>{globalComplianceScore !== null ? `${globalComplianceScore}%` : 'N/A'}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Status:</span>
              <p className="text-green-600">Active</p>
            </div>
          </div>
        </div>
      </div>

      {/* Evidence Analysis Section */}
      {sopData?.evidence_analysis && (
        <div className="bg-white border overflow-hidden rounded-lg border-l-4 border-amber-800">
          <button
            onClick={() => setIsEvidenceExpanded(!isEvidenceExpanded)}
            className="w-full px-6 py-4 bg-gradient-to-r from-green-50 to-white border-b border-gray-200 flex items-center justify-between hover:bg-green-50 transition-colors"
          >
            <div className="flex items-center">
              <Info className="w-5 h-5 mr-2" />
              <span className="font-medium">Evidence Analysis</span>
              <Tooltip text="Analysis of information gaps, assumptions made, and source attribution for audit trail and compliance purposes." placement="top">
                <Info className="inline-block align-middle w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
              {/* Count badges */}
              <div className="flex items-center ml-4 space-x-2">
                {sopData.evidence_analysis.information_gaps?.length > 0 && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                    {sopData.evidence_analysis.information_gaps.length} Gaps
                  </span>
                )}
                {sopData.evidence_analysis.assumptions_made?.length > 0 && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                    {sopData.evidence_analysis.assumptions_made.length} Assumptions
                  </span>
                )}
                {sopData.evidence_analysis.source_attribution?.length > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {sopData.evidence_analysis.source_attribution.length} Sources
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center text-gray-500">
              <span className="text-sm mr-2">{isEvidenceExpanded ? 'Collapse' : 'Expand'}</span>
              {isEvidenceExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </button>

          {/* Collapsible Content */}
          {isEvidenceExpanded && (
            <>
          {/* Information Gaps */}
          {sopData?.evidence_analysis?.information_gaps && sopData.evidence_analysis.information_gaps.length > 0 && (
            <div className="mb-6">
              <h5 className="font-medium text-red-700 mb-2 flex items-center p-4">
                <AlertCircle className="w-4 h-4 mr-1" />
                Information Gaps Requiring SME Input
              </h5>
              <div className="space-y-3 p-4">
                {sopData.evidence_analysis.information_gaps.map((gap: any, idx: number) => (
                  <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-red-800">
                        {gap.gap_type || 'Missing Information'}
                      </span>
                      <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded">
                        {gap.section_affected || 'General'}
                      </span>
                    </div>
                    <p className="text-sm text-red-700 mb-2">{gap.description}</p>
                    {gap.sme_input_required && (
                      <p className="text-xs text-red-600 italic">
                        <strong>SME Question:</strong> {gap.sme_input_required}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="px-4 pb-4">
                <button
                  onClick={async () => {
                    if (!currentProject) return;
                    try {
                      toast.loading('Syncing gaps to interview questions...', { id: 'gap-sync' });
                      const response = await api.post(`/api/analysis/gap-sync/${currentProject.id}`);
                      toast.success(`Generated ${response.data.questions_generated} interview questions from ${response.data.gaps_detected} gaps`, { id: 'gap-sync' });
                    } catch (error) {
                      toast.error('Failed to sync gaps', { id: 'gap-sync' });
                    }
                  }}
                  className="w-full bg-gradient-to-r from-red-600 to-orange-600 text-white px-4 py-2 rounded-lg hover:from-red-700 hover:to-orange-700 transition-all flex items-center justify-center space-x-2"
                >
                  <AlertCircle className="w-4 h-4" />
                  <span>Auto-Generate Interview Questions from Gaps</span>
                </button>
              </div>
            </div>
          )}

          {/* Assumptions Made */}
          {sopData?.evidence_analysis?.assumptions_made && sopData.evidence_analysis.assumptions_made.length > 0 && (
            <div className="mb-6">
              <h5 className="font-medium text-yellow-700 mb-2 flex items-center p-4">
                <AlertTriangle className="w-4 h-4 mr-1" />
                Assumptions Made
              </h5>
              <div className="space-y-3 px-4">
                {sopData.evidence_analysis.assumptions_made.map((assumption: any, idx: number) => (
                  <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-yellow-800 mb-1">{assumption.assumption}</p>
                    <p className="text-xs text-yellow-700 mb-2">{assumption.rationale}</p>
                    {assumption.validation_needed && (
                      <p className="text-xs text-yellow-600 italic">
                        <strong>Validation Needed:</strong> {assumption.validation_needed}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Source Attribution */}
          {sopData?.evidence_analysis?.source_attribution && sopData.evidence_analysis.source_attribution.length > 0 && (
            <div>
              <h5 className="font-medium text-blue-700 mb-2 flex items-center p-4">
                <FileText className="w-4 h-4 mr-1" />
                Source Attribution
              </h5>
              <div className="space-y-2 px-4 pb-4">
                {sopData.evidence_analysis.source_attribution.map((source: any, idx: number) => (
                  <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-blue-800">
                        {source.source_document || 'Unknown Document'}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${source.confidence_level === 'high' ? 'bg-green-100 text-green-800' :
                        source.confidence_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                        {source.confidence_level || 'unknown'} confidence
                      </span>
                    </div>
                    <p className="text-xs text-blue-600">
                      Section: {source.section || 'General'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
            </>
          )}
        </div>
      )}

      {/* Gap Sync Info - Automatic gap detection enabled */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="font-semibold text-green-800 mb-1">✓ Automatic Gap Detection Enabled</h4>
            <p className="text-sm text-green-700 mb-2">
              Gaps are automatically detected from SOP, Process Map, and Process Synthesis whenever you generate or regenerate an SOP.
              Interview questions are automatically created to fill these gaps.
            </p>
            <div className="flex items-center space-x-2 text-xs text-green-600">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span>Check the <strong>Fill Gaps (Interviews)</strong> tab to see auto-generated questions</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gaps & Deviations for SOP */}


      {/* SOP Marker Legend */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
        <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
          <Info className="w-4 h-4 mr-2" />
          Comprehensive Enhanced SOP Markers Guide
        </h4>
        {sopType === 'enhanced' && (
          <p className="text-sm text-gray-600 mb-3">
            This comprehensive SOP contains detailed 20k+ word documentation with extensive procedural details,
            control points, and compliance requirements for enterprise-grade operations.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 bg-red-500 rounded-full mr-2"></span>
            <span className="text-red-800 font-medium">GAP IDENTIFIED</span>
          </div>
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 bg-purple-500 rounded-full mr-2"></span>
            <span className="text-purple-800 font-medium">CONTROL POINT</span>
          </div>
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
            <span className="text-blue-800 font-medium">SCREENSHOT NEEDED</span>
          </div>
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span>
            <span className="text-green-800 font-medium">FLOWCHART NEEDED</span>
          </div>
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 bg-orange-500 rounded-full mr-2"></span>
            <span className="text-orange-800 font-medium">CONFLICT</span>
          </div>
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 bg-gray-500 rounded-full mr-2"></span>
            <span className="text-gray-800 font-medium">SOURCE</span>
          </div>
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 bg-yellow-500 rounded-full mr-2"></span>
            <span className="text-yellow-800 font-medium">ASSUMPTION</span>
          </div>
        </div>
      </div>

      {/* Render SOP Content */}
      <div className="space-y-4">
        {/* Auto-generate Key Definitions Button */}
        {terminologyDefinitions.length > 0 ? (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FileText className="w-5 h-5 text-purple-600" />
                <div>
                  <h4 className="text-sm font-semibold text-purple-900">Auto-Generate Key Definitions</h4>
                  <p className="text-xs text-purple-700">
                    {terminologyDefinitions.length} approved terminology term{terminologyDefinitions.length !== 1 ? 's' : ''} available
                  </p>
                </div>
              </div>
              <button
                onClick={handleAutoGenerateKeyDefinitions}
                disabled={isLoadingTerminology}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors"
              >
                <Wand2 className="w-4 h-4" />
                <span>{isLoadingTerminology ? 'Generating...' : 'Generate Section'}</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-2">
              <Info className="w-5 h-5 text-gray-500" />
              <div>
                <h4 className="text-sm font-semibold text-gray-700">No Approved Terminology Found</h4>
                <p className="text-xs text-gray-600">
                  Please approve terminology from the Terminology Analysis page to enable Key Definitions auto-generation.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Aligned/Deviation Areas summary with tooltip */}
        {sopData?.template_alignment && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
            <div className="flex items-center mb-2">
              <span className="font-semibold text-green-700 mr-2">Aligned Areas</span>
              <Tooltip text={`"Aligned Areas" are process steps or controls that match the uploaded template's requirements and best practices.\n\nFactors considered:\n- Direct matches to template sections\n- Use of required controls, approvals, and roles\n- Adherence to regulatory and industry standards\n\nRisk frameworks referenced: COSO, SOX, Basel III, and any uploaded templates.`} placement="top">
                <Info className="inline-block align-middle w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
            </div>
            <ul className="list-disc ml-6 text-green-900">
              {sopData?.template_alignment?.aligned_areas?.length ? sopData.template_alignment.aligned_areas.map((area: string, idx: number) => (
                <li key={idx}>{area}</li>
              )) : <li>No aligned areas found.</li>}
            </ul>
            <div className="flex items-center mt-4 mb-2">
              <span className="font-semibold text-red-700 mr-2">Deviation Areas</span>
              <Tooltip text={`"Deviation Areas" are steps or controls that do not fully align with the template or best practices.\n\nFactors considered:\n- Missing required sections or controls\n- Gaps in compliance with regulatory or template standards\n- Unusual process flows or lack of documentation\n\nRisk frameworks referenced: COSO, SOX, Basel III, and any uploaded templates.`} placement="top">
                <Info className="inline-block align-middle w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
            </div>
            <ul className="list-disc ml-6 text-red-900">
              {sopData?.template_alignment?.deviation_areas?.length ? sopData.template_alignment.deviation_areas.map((area: string, idx: number) => (
                <li key={idx}>{area}</li>
              )) : <li>No deviation areas found.</li>}
            </ul>
          </div>
        )}
        {/* Toggle View for Content Transparency */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Eye className="w-5 h-5 text-gray-600" />
              <span className="font-medium text-gray-700">Content View:</span>
            </div>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setContentViewMode('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  contentViewMode === 'all'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Show All
              </button>
              <button
                onClick={() => setContentViewMode('original')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  contentViewMode === 'original'
                    ? 'bg-green-100 text-green-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                ✓ Original Only
              </button>
              <button
                onClick={() => setContentViewMode('additions')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  contentViewMode === 'additions'
                    ? 'bg-violet-100 text-violet-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                + AI Added Only
              </button>
            </div>
          </div>
          {contentViewMode !== 'all' && (
            <p className="text-xs text-gray-500 mt-2">
              {contentViewMode === 'original'
                ? 'Showing only content from your original documents (marked with ✓ Original)'
                : 'Showing only content added by AI (marked with + AI Added)'}
            </p>
          )}
        </div>

        {sopData?.sections?.map((section: any, index: number) => {
          const isEditing = editingSectionIndex === index;

          // Check both frontend state and backend status fields
          // modification_status: user edit tracking (user_modified, user_approved)
          // validation_status: preservation tracking (human_verified, human_modified, ai_generated)
          const backendModificationStatus = section.modification_status;
          const backendValidationStatus = section.validation_status;
          const frontendStatus = editedSections[index]?.status;

          // Priority: frontend state > modification_status > validation_status
          const actualStatus: string = frontendStatus || backendModificationStatus || backendValidationStatus || 'ai_generated';

          const isModified = actualStatus === 'user_modified';
          const isApproved = actualStatus === 'user_approved';
          const isVerified = actualStatus === 'human_verified' || section.preserved === true;
          const isHumanModified = actualStatus === 'human_modified';
          const isAIGenerated = actualStatus === 'ai_generated' && !isVerified;

          // Get current content and ensure it's plain text (strip any HTML tags)
          let currentContent = editedSections[index]?.content || section.content;

          // If content contains HTML tags, strip them to ensure proper rendering through SOPContentRenderer
          if (typeof currentContent === 'string' && (currentContent.includes('<p>') || currentContent.includes('<br>'))) {
            // Strip HTML tags but preserve newlines
            currentContent = currentContent
              .replace(/<br\s*\/?>/gi, '\n')
              .replace(/<\/p>\s*<p>/gi, '\n\n')
              .replace(/<p>/gi, '')
              .replace(/<\/p>/gi, '')
              .replace(/<[^>]+>/g, '')
              .replace(/&nbsp;/g, ' ')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&');
          }

          // FIX: Convert emoji-based markers to proper bracket format
          // This handles corrupted data from previous edits where markers lost their brackets
          if (typeof currentContent === 'string') {
            // Convert emoji-prefixed markers (with or without bold formatting) to proper bracket format
            currentContent = currentContent
              // DOCUMENTED marker
              .replace(/📋\*\*DOCUMENTED:\*\*/g, '[DOCUMENTED:')
              .replace(/📋DOCUMENTED:/g, '[DOCUMENTED:')
              // INFERRED marker
              .replace(/🔍\*\*INFERRED:\*\*/g, '[INFERRED:')
              .replace(/🔍INFERRED:/g, '[INFERRED:')
              // ASSUMED marker
              .replace(/💭\*\*ASSUMED:\*\*/g, '[ASSUMED:')
              .replace(/💭ASSUMED:/g, '[ASSUMED:')
              // CONTROL POINT marker
              .replace(/🔍\*\*CONTROL POINT:\*\*/g, '[CONTROL POINT:')
              .replace(/🔍CONTROL POINT:/g, '[CONTROL POINT:')
              // GAP IDENTIFIED marker
              .replace(/⚠️\*\*GAP IDENTIFIED:\*\*/g, '[GAP IDENTIFIED:')
              .replace(/⚠️GAP IDENTIFIED:/g, '[GAP IDENTIFIED:')
              // SCREENSHOT NEEDED marker
              .replace(/📸\*\*SCREENSHOT NEEDED:\*\*/g, '[SCREENSHOT NEEDED:')
              .replace(/📸SCREENSHOT NEEDED:/g, '[SCREENSHOT NEEDED:')
              // FLOWCHART NEEDED marker
              .replace(/📊\*\*FLOWCHART NEEDED:\*\*/g, '[FLOWCHART NEEDED:')
              .replace(/📊FLOWCHART NEEDED:/g, '[FLOWCHART NEEDED:')
              // CONFLICT marker
              .replace(/⚡\*\*CONFLICT:\*\*/g, '[CONFLICT:')
              .replace(/⚡CONFLICT:/g, '[CONFLICT:')
              // SOURCE marker
              .replace(/📄\*\*SOURCE:\*\*/g, '[SOURCE:')
              .replace(/📄SOURCE:/g, '[SOURCE:')
              // ASSUMPTION marker
              .replace(/💡\*\*ASSUMPTION:\*\*/g, '[ASSUMPTION:')
              .replace(/💡ASSUMPTION:/g, '[ASSUMPTION:');

            // Remove bold formatting from marker labels: [**DOCUMENTED:**text] -> [DOCUMENTED:text]
            currentContent = currentContent
              .replace(/\[\*\*DOCUMENTED:\*\*/g, '[DOCUMENTED:')
              .replace(/\[\*\*INFERRED:\*\*/g, '[INFERRED:')
              .replace(/\[\*\*ASSUMED:\*\*/g, '[ASSUMED:')
              .replace(/\[\*\*CONTROL POINT:\*\*/g, '[CONTROL POINT:')
              .replace(/\[\*\*GAP IDENTIFIED:\*\*/g, '[GAP IDENTIFIED:')
              .replace(/\[\*\*SCREENSHOT NEEDED:\*\*/g, '[SCREENSHOT NEEDED:')
              .replace(/\[\*\*FLOWCHART NEEDED:\*\*/g, '[FLOWCHART NEEDED:')
              .replace(/\[\*\*CONFLICT:\*\*/g, '[CONFLICT:')
              .replace(/\[\*\*SOURCE:\*\*/g, '[SOURCE:')
              .replace(/\[\*\*ASSUMPTION:\*\*/g, '[ASSUMPTION:');

            // Add closing brackets if missing
            // Strategy: Look for [MARKER: that's NOT followed by ] before the next sentence/marker/newline
            // Match patterns:
            // 1. [MARKER:text that goes to end of line
            // 2. [MARKER:text until next marker starts
            // 3. [MARKER:text. (until period + space or newline)
            currentContent = currentContent.replace(
              /(\[(?:DOCUMENTED|INFERRED|ASSUMED|CONTROL POINT|GAP IDENTIFIED|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION):)([^\]]+?)(?=(?:\.\s|\n|$|\s+\[(?:DOCUMENTED|INFERRED|ASSUMED|CONTROL POINT|GAP IDENTIFIED|SCREENSHOT NEEDED|FLOWCHART NEEDED|CONFLICT|SOURCE|ASSUMPTION):))/g,
              '$1$2]'
            );
          }
          const lastEditedAt = section.last_edited_at;

          // Get status badge styling
          const getStatusBadge = () => {
            if (isApproved) {
              return {
                bg: 'bg-green-100',
                text: 'text-green-700',
                border: 'border-green-200',
                icon: '✅',
                label: 'Approved'
              };
            } else if (isModified) {
              return {
                bg: 'bg-yellow-100',
                text: 'text-yellow-700',
                border: 'border-yellow-200',
                icon: '✏️',
                label: 'Modified'
              };
            } else if (isVerified || isHumanModified) {
              return {
                bg: 'bg-blue-100',
                text: 'text-blue-700',
                border: 'border-blue-200',
                icon: '✓',
                label: isVerified ? 'Verified (Preserved)' : 'Human Modified'
              };
            } else {
              return {
                bg: 'bg-purple-100',
                text: 'text-purple-700',
                border: 'border-purple-200',
                icon: '🤖',
                label: 'AI Generated'
              };
            }
          };

          const statusBadge = getStatusBadge();

          return (
            <React.Fragment key={index}>
              <div
                className={`bg-white border-2 rounded-lg p-6 ${
                  isApproved ? 'border-green-300 bg-green-50' :
                  isModified ? 'border-yellow-400 bg-yellow-50' :
                  (isVerified || isHumanModified) ? 'border-blue-300 bg-blue-50' :
                  'border-gray-200'
                }`}
              >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold text-super-dark-purple flex items-center">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm mr-3">
                    {section.section_number || `${index + 1}`}
                  </span>
                  {section.section_title || section.title}
                </h4>
                <div className="flex items-center space-x-2">
                  {/* Status Badge with Icon */}
                  <span
                    className={`text-xs ${statusBadge.bg} ${statusBadge.text} border ${statusBadge.border} px-3 py-1 rounded-full font-medium flex items-center gap-1.5`}
                    title={`Status: ${statusBadge.label}${lastEditedAt ? `\nLast edited: ${new Date(lastEditedAt).toLocaleString()}` : ''}`}
                  >
                    <span>{statusBadge.icon}</span>
                    <span>{statusBadge.label}</span>
                  </span>

                  {/* Timestamp indicator for modified sections */}
                  {(isModified || isApproved) && lastEditedAt && (
                    <span className="text-xs text-gray-500 italic" title={new Date(lastEditedAt).toLocaleString()}>
                      <Clock className="w-3 h-3 inline mr-1" />
                      {(() => {
                        const now = new Date();
                        const edited = new Date(lastEditedAt);
                        const diffMinutes = Math.floor((now.getTime() - edited.getTime()) / 60000);
                        if (diffMinutes < 1) return 'Just now';
                        if (diffMinutes < 60) return `${diffMinutes}m ago`;
                        const diffHours = Math.floor(diffMinutes / 60);
                        if (diffHours < 24) return `${diffHours}h ago`;
                        return `${Math.floor(diffHours / 24)}d ago`;
                      })()}
                    </span>
                  )}

                  {!isEditing && (
                    <button
                      onClick={() => handleEditSection(index, section.content)}
                      className="text-gray-600 hover:text-purple-600 transition-colors p-2"
                      title="Edit section"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {isEditing ? (
                <div className="space-y-3">
                  <RichTextEditor
                    content={currentContent}
                    onChange={(newContent) => handleContentChange(index, newContent)}
                    placeholder="Edit section content..."
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Auto-saves after 30 seconds</span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleCancelEdit(index)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveSection(index)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm flex items-center space-x-2"
                      >
                        <Save className="w-4 h-4" />
                        <span>Save</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Conditional rendering for Key Definitions section
                (section.section_title === 'Key Definitions' || section.title === 'Key Definitions') ? (
                  // If terminology available, show ONLY terminology (hide AI content)
                  terminologyDefinitions.length > 0 ? (
                    <div>
                      <p className="font-semibold mb-2 p-1">
                        From Terminology Analysis ({terminologyDefinitions.length} terms)
                      </p>
                      {terminologyDefinitions.map((term: any) => (
                        <div key={term.id} className="flex p-1 items-start justify-between group hover:bg-gray-50 rounded transition-colors mb-3">
                          <p className="flex-1">
                            <span className="bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded">{term.term}:</span>
                            <span className="ml-2 text-gray-700">{term.definition}</span>
                          </p>
                          <button
                            onClick={() => deleteTerminologyDefinition(term.id)}
                            className="ml-2 text-red-500  transition-opacity flex-shrink-0"
                            title="Remove from SOP"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // No terminology yet, show AI-generated content with SOPContentRenderer
                    <SOPContentRenderer content={currentContent} viewMode={contentViewMode} />
                  )
                ) : (
                  // For all other sections, always use SOPContentRenderer for proper marker display
                  <SOPContentRenderer content={currentContent} viewMode={contentViewMode} />
                )
              )}
              </div>

              {/* REQ-SOP-2.3: Add Section Button */}
              <div className="flex items-center justify-center my-4">
                <button
                  onClick={() => openAddSectionModal(index)}
                  className="group flex items-center space-x-2 px-4 py-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors border-2 border-dashed border-purple-300 hover:border-purple-400"
                  title="Add custom section here"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm font-medium">Add Section</span>
                </button>
              </div>
            </React.Fragment>
          );
        })}

        {/* Add Section Modal */}
        <AddSectionModal
          isOpen={isAddSectionModalOpen}
          onClose={() => {
            setIsAddSectionModalOpen(false);
            setInsertAfterIndex(null);
          }}
          onAdd={handleAddSection}
          insertAfterSection={insertAfterIndex !== null && sopData?.sections?.[insertAfterIndex]?.section_number}
        />
      </div>
      </>
      )}

      {/* End-to-End Process Flow (shared component) */}
      {/* <EndToEndProcessFlow
        steps={steps}
        onStepsChange={(newSteps) => {
          setSteps(newSteps);
          saveSteps(newSteps);
        }}
        isSaving={isSaving}
        saveQueued={saveQueued}
        onChatOpen={(stepIndex, stepText) => {
          setChatStepIdx(stepIndex);
          setChatOpen(true);
        }}
        projectId={currentProject?.id || synthesis?.project_id}
      /> */}


      {/* Manual Section Creation */}
      {steps.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div
            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors border-l-4 border-blue-500 flex items-center justify-between"
            onClick={() => setIsSectionManagementCollapsed(!isSectionManagementCollapsed)}
          >
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                {isSectionManagementCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                <Settings className="w-5 h-5 text-gray-600" />
                <h3 className="font-semibold text-gray-800">Section Management</h3>
              </div>
              <span className="text-sm text-gray-600">(Organize steps into sections)</span>
            </div>
            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
              {sections.length} sections
            </span>
          </div>

          {!isSectionManagementCollapsed && (
            <div className="p-4 pt-0">
              <div className="flex items-center justify-between mb-4 mt-4">
                <h4 className="font-medium text-gray-700">Create and manage sections</h4>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAddSection(!showAddSection);
                  }}
                  className="flex items-center space-x-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Section</span>
                </button>
              </div>

          {/* Add New Section Form */}
          {showAddSection && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-3">Create New Section</h4>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  placeholder="Section name (e.g., 'Initiation', 'Processing', 'Validation')"
                  className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && newSectionName.trim() && addSection()}
                />
                <input
                  type="text"
                  value={newSectionDescription}
                  onChange={(e) => setNewSectionDescription(e.target.value)}
                  placeholder="Section description (optional)"
                  className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && newSectionName.trim() && addSection()}
                />
                <div className="flex space-x-2">
                  <button
                    onClick={addSection}
                    disabled={!newSectionName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Create Section</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowAddSection(false);
                      setNewSectionName('');
                      setNewSectionDescription('');
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Existing Sections List */}
          {sections.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">Current Sections:</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {sections.map(section => (
                  <div key={section.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: section.color }}
                      />
                      <div>
                        <span className="font-medium">{section.name}</span>
                        {section.description && (
                          <p className="text-sm text-gray-500">{section.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {stepsBySection.get(section.id)?.length || 0} steps
                      </span>
                      <button
                        onClick={() => deleteSection(section.id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                        title="Delete section"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Process Sections with Steps */}
      {(sections.length > 0 || steps.length > 0) && (
        <div className="space-y-4">
          {sections.map(section => {
            const sectionSteps = stepsBySection.get(section.id) || [];
            const isExpanded = expandedSections.has(section.id);

            return (
              <div key={section.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                {/* Section Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    const newExpanded = new Set(expandedSections);
                    if (isExpanded) {
                      newExpanded.delete(section.id);
                    } else {
                      newExpanded.add(section.id);
                    }
                    setExpandedSections(newExpanded);
                  }}
                  style={{ borderLeftColor: section.color, borderLeftWidth: '4px' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                        <FolderOpen className="w-5 h-5" style={{ color: section.color }} />
                        <h3 className="font-semibold text-gray-800">{section.name}</h3>
                      </div>
                      {section.description && (
                        <span className="text-sm text-gray-500">({section.description})</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                        {sectionSteps.length} steps
                      </span>
                    </div>
                  </div>
                </div>

                {/* Section Steps */}
                {isExpanded && (
                  <div
                    className="p-4 pt-0 space-y-2 min-h-[100px] border-2 border-dashed border-gray-200 bg-gray-50"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleSectionContainerDrop(e, section.id)}
                  >
                    {sectionSteps.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <FolderOpen className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p>Drop steps here</p>
                        <p className="text-xs">Drag steps from "End-to-End Process Flow" or other sections</p>
                      </div>
                    ) : (
                      sectionSteps.map(({ step, index }, positionInSection) => {
                        const stepText = typeof step === 'string' ? step : step.step;
                        const isDragging = draggedStepId === index;

                        return (
                          <div
                            key={`section_step_${index}`}
                            draggable
                            onDragStart={(e) => handleSectionStepDragStart(e, index, section.id)}
                            onDragOver={handleSectionStepDragOver}
                            onDrop={(e) => handleSectionStepDrop(e, index, section.id)}
                            className={`p-3 bg-white rounded-md border transition-all cursor-move shadow-sm ${isDragging
                              ? 'border-blue-300 bg-blue-50 opacity-50'
                              : 'border-gray-200 hover:border-gray-300'
                              }`}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="mt-1 text-gray-400 hover:text-gray-600">
                                <GripVertical className="w-4 h-4" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-start justify-between">
                                  <span className="text-sm font-medium text-gray-600">
                                    Step {getProperStepNumber(index)}
                                  </span>
                                  <div className="flex items-center space-x-1">
                                    <button
                                      onClick={() => {
                                        setEditingStep(index);
                                        setEditValue(stepText);
                                      }}
                                      className="text-gray-400 hover:text-blue-600 transition-colors"
                                      title="Edit step"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => deleteStep(index)}
                                      className="text-gray-400 hover:text-red-600 transition-colors"
                                      title="Delete step"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setChatStepIdx(index);
                                        setChatOpen(true);
                                      }}
                                      className="text-gray-400 hover:text-green-600 transition-colors"
                                      title="Ask Jarvis about this step"
                                    >
                                      <MessageCircle className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                                {editingStep === index ? (
                                  <div className="mt-2 space-y-2">
                                    <textarea
                                      ref={(el) => {
                                        if (el && editingStep === index) {
                                          // Focus and enable text selection
                                          el.focus();
                                          el.style.pointerEvents = 'auto';
                                          el.style.userSelect = 'text';
                                          el.style.webkitUserSelect = 'text';
                                          (el.style as any).MozUserSelect = 'text';
                                          (el.style as any).msUserSelect = 'text';
                                          el.style.cursor = 'text';

                                          // Set cursor to end of text for better UX
                                          setTimeout(() => {
                                            el.setSelectionRange(el.value.length, el.value.length);
                                          }, 50);
                                        }
                                      }}
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        // Handle keyboard shortcuts
                                        if (e.key === 'Enter' && e.ctrlKey) {
                                          e.preventDefault();
                                          // Trigger save button click
                                          e.currentTarget.parentElement?.querySelector('button')?.click();
                                        } else if (e.key === 'Escape') {
                                          e.preventDefault();
                                          setEditingStep(null);
                                          setEditValue('');
                                        }
                                        // Allow normal text operations
                                        if (!['Enter', 'Escape', 'Tab'].includes(e.key)) {
                                          e.stopPropagation();
                                        }
                                      }}
                                      onMouseDown={(e) => {
                                        // Ensure mouse events work for text selection
                                        e.stopPropagation();
                                      }}
                                      onDoubleClick={(e) => {
                                        // Enable double-click to select words
                                        e.stopPropagation();
                                      }}
                                      className="w-full p-3 border border-gray-300 rounded-md resize-none focus:outline-none 
             focus:ring-2 focus:ring-blue-500 focus:border-blue-500
             min-h-[100px] text-sm leading-relaxed bg-white
             selection:bg-blue-200 selection:text-blue-900"
                                      style={{
                                        pointerEvents: 'auto',
                                        userSelect: 'text',
                                        WebkitUserSelect: 'text',
                                        cursor: 'text'
                                      } as React.CSSProperties}
                                      rows={4}
                                      placeholder="Enter step description..."
                                    />
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={async () => {
                                          if (!currentProject?.id) {
                                            toast.error('No project selected');
                                            return;
                                          }

                                          try {
                                            console.log('🔄 EnhancedProcessSynthesisTab: Saving edited step...', { index, editValue: editValue.substring(0, 50) + '...' });

                                            const updatedSteps = [...steps];
                                            if (typeof updatedSteps[index] === 'string') {
                                              updatedSteps[index] = editValue;
                                            } else {
                                              (updatedSteps[index] as ProcessStep).step = editValue;
                                            }

                                            // Update local state immediately
                                            setSteps(updatedSteps);
                                            setEditingStep(null);
                                            setEditValue('');

                                            // CRITICAL: Save to backend immediately
                                            await api.put(`/api/analysis/process-steps/${currentProject.id}`, {
                                              steps: updatedSteps
                                            });

                                            console.log('✅ EnhancedProcessSynthesisTab: Step saved to backend successfully');

                                            // CRITICAL: Trigger global refresh for tab synchronization
                                            if (onRefresh) {
                                              console.log('🔄 EnhancedProcessSynthesisTab: Triggering global refresh for tab sync...');
                                              setTimeout(() => {
                                                onRefresh();
                                              }, 200); // Small delay to ensure backend save completes
                                            }

                                            toast.success('Step updated and synchronized across tabs', {
                                              duration: 3000,
                                              icon: '✅'
                                            });

                                          } catch (error) {
                                            console.error('❌ EnhancedProcessSynthesisTab: Failed to save step:', error);
                                            toast.error('Failed to save step. Please try again.');

                                            // Revert local state on error
                                            setEditingStep(index);
                                            setEditValue(editValue);
                                          }
                                        }}
                                        className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingStep(null);
                                          setEditValue('');
                                        }}
                                        className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-700 mt-1">{stepText}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
            </div>
          )}
        </div>
      )}

      {/* Unassigned Steps */}
      {unassignedSteps.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div
            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors border-l-4 border-amber-500"
            onClick={() => setUnassignedStepsExpanded(!unassignedStepsExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  {unassignedStepsExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  <Folder className="w-5 h-5 text-gray-600" />
                  <h3 className="font-semibold text-gray-800">End-to-End Process Flow</h3>
                </div>
                <span className="text-sm text-gray-600">(Drag these to sections)</span>
              </div>
              <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                {unassignedSteps.length} steps
              </span>
            </div>
          </div>

          {unassignedStepsExpanded && (
            <div className="p-4 pt-0 space-y-2 bg-gray-50">
              {unassignedSteps.map(({ step, index }) => {
                const stepText = typeof step === 'string' ? step : step.step;

                return (
                  <div
                    key={`unassigned_step_${index}`}
                    draggable
                    onDragStart={(e) => handleMainFlowDragStart(e, index)}
                    className="p-3 bg-white rounded-md border border-gray-200 hover:border-gray-300 transition-colors cursor-move shadow-sm"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="mt-1 text-gray-400 hover:text-gray-600">
                        <GripVertical className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <span className="text-sm font-medium text-gray-700">
                            Step {index + 1}
                          </span>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => {
                                setEditingStep(index);
                                setEditValue(stepText);
                              }}
                              className="text-gray-400 hover:text-blue-600 transition-colors"
                              title="Edit step"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteStep(index)}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete step"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setChatStepIdx(index);
                                setChatOpen(true);
                              }}
                              className="text-gray-400 hover:text-green-600 transition-colors"
                              title="Ask Jarvis about this step"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {editingStep === index ? (
                          <div className="mt-2 space-y-2">
                            <textarea
                              ref={(el) => {
                                if (el && editingStep === index) {
                                  // Focus and enable text selection
                                  el.focus();
                                  el.style.pointerEvents = 'auto';
                                  el.style.userSelect = 'text';
                                  el.style.webkitUserSelect = 'text';
                                  (el.style as any).MozUserSelect = 'text';
                                  (el.style as any).msUserSelect = 'text';
                                  el.style.cursor = 'text';

                                  // Set cursor to end of text for better UX
                                  setTimeout(() => {
                                    el.setSelectionRange(el.value.length, el.value.length);
                                  }, 50);
                                }
                              }}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                // Handle keyboard shortcuts
                                if (e.key === 'Enter' && e.ctrlKey) {
                                  e.preventDefault();
                                  // Trigger save button click
                                  e.currentTarget.parentElement?.querySelector('button')?.click();
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  setEditingStep(null);
                                  setEditValue('');
                                }
                                // Allow normal text operations
                                if (!['Enter', 'Escape', 'Tab'].includes(e.key)) {
                                  e.stopPropagation();
                                }
                              }}
                              onMouseDown={(e) => {
                                // Ensure mouse events work for text selection
                                e.stopPropagation();
                              }}
                              onDoubleClick={(e) => {
                                // Enable double-click to select words
                                e.stopPropagation();
                              }}
                              className="w-full p-3 border border-gray-300 rounded-md resize-none focus:outline-none 
             focus:ring-2 focus:ring-blue-500 focus:border-blue-500
             min-h-[100px] text-sm leading-relaxed bg-white
             selection:bg-blue-200 selection:text-blue-900"
                              style={{
                                pointerEvents: 'auto',
                                userSelect: 'text',
                                WebkitUserSelect: 'text',
                                cursor: 'text'
                              } as React.CSSProperties}
                              rows={4}
                              placeholder="Enter step description..."
                            />
                            <div className="flex space-x-2">
                              <button
                                onClick={async () => {
                                  if (!currentProject?.id) {
                                    toast.error('No project selected');
                                    return;
                                  }

                                  try {
                                    console.log('🔄 EnhancedProcessSynthesisTab: Saving edited step...', { index, editValue: editValue.substring(0, 50) + '...' });

                                    const updatedSteps = [...steps];
                                    if (typeof updatedSteps[index] === 'string') {
                                      updatedSteps[index] = editValue;
                                    } else {
                                      (updatedSteps[index] as ProcessStep).step = editValue;
                                    }

                                    // Update local state immediately
                                    setSteps(updatedSteps);
                                    setEditingStep(null);
                                    setEditValue('');

                                    // CRITICAL: Save to backend immediately
                                    await api.put(`/api/analysis/process-steps/${currentProject.id}`, {
                                      steps: updatedSteps
                                    });

                                    console.log('✅ EnhancedProcessSynthesisTab: Step saved to backend successfully');

                                    // CRITICAL: Trigger global refresh for tab synchronization
                                    if (onRefresh) {
                                      console.log('🔄 EnhancedProcessSynthesisTab: Triggering global refresh for tab sync...');
                                      setTimeout(() => {
                                        onRefresh();
                                      }, 200); // Small delay to ensure backend save completes
                                    }

                                    toast.success('Step updated and synchronized across tabs', {
                                      duration: 3000,
                                      icon: '✅'
                                    });

                                  } catch (error) {
                                    console.error('❌ EnhancedProcessSynthesisTab: Failed to save step:', error);
                                    toast.error('Failed to save step. Please try again.');

                                    // Revert local state on error
                                    setEditingStep(index);
                                    setEditValue(editValue);
                                  }
                                }}
                                className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingStep(null);
                                  setEditValue('');
                                }}
                                className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="mt-1 text-gray-700">{stepText}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Stakeholders & Systems - HIDDEN - Moved to Analysis > Stakeholders & Systems tab */}
      {false && <>
      {/* Stakeholders & Responsibilities */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-white border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Users className="w-5 h-5 text-electric-blue" />
            <h3 className="text-lg font-semibold">Stakeholders & Responsibilities</h3>
          </div>
          <div className="flex items-center space-x-2">
            {selectedStakeholders.length > 1 && (
              <button
                onClick={mergeStakeholders}
                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-md text-sm flex items-center space-x-1"
                title={`Merge ${selectedStakeholders.length} selected stakeholders`}
              >
                <Merge className="w-4 h-4" />
                <span>Merge ({selectedStakeholders.length})</span>
              </button>
            )}
            <button
              onClick={() => setShowAddStakeholder(true)}
              className="bg-electric-blue hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm flex items-center space-x-1"
              title="Add new stakeholder"
            >
              <Plus className="w-4 h-4" />
              <span>Add Stakeholder</span>
            </button>
            <button
              onClick={() => setIsStakeholdersCollapsed(!isStakeholdersCollapsed)}
              className={`p-2 rounded-full border-2 border-electric-blue bg-white shadow-lg hover:bg-electric-blue hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 ml-2 flex items-center justify-center`}
              style={{ minWidth: 40, minHeight: 40 }}
              title={isStakeholdersCollapsed ? 'Expand Stakeholders' : 'Collapse Stakeholders'}
            >
              {isStakeholdersCollapsed ? <ChevronDown className="w-7 h-7" /> : <ChevronUp className="w-7 h-7" />}
            </button>
          </div>
        </div>
        {!isStakeholdersCollapsed && (
          <div className="p-6">
            {/* Add Stakeholder Form */}
            {showAddStakeholder && (
              <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200">
                <h4 className="font-medium mb-3">Add New Stakeholder</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Role/Title"
                    value={newStakeholder.role}
                    onChange={(e) => setNewStakeholder({ ...newStakeholder, role: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Responsibility"
                    value={newStakeholder.responsibility}
                    onChange={(e) => setNewStakeholder({ ...newStakeholder, responsibility: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div className="flex space-x-2 mt-3">
                  <button onClick={addStakeholder} className="bg-green-600 text-white px-3 py-1 rounded text-sm">
                    Add
                  </button>
                  <button onClick={() => setShowAddStakeholder(false)} className="bg-gray-400 text-white px-3 py-1 rounded text-sm">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {Object.keys(stakeholders).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(stakeholders).map(([role, responsibility]: [string, any]) => (
                  <div key={role} className={`bg-white p-4 rounded-lg border-l-4 border-electric-blue group relative ${selectedStakeholders.includes(role) ? 'ring-2 ring-purple-500' : ''
                    }`}>
                    <input
                      type="checkbox"
                      checked={selectedStakeholders.includes(role)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStakeholders([...selectedStakeholders, role]);
                        } else {
                          setSelectedStakeholders(selectedStakeholders.filter(r => r !== role));
                        }
                      }}
                      className="absolute top-2 right-2 w-4 h-4"
                    />

                    {editingStakeholder === role ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editStakeholderValue.role}
                          onChange={(e) => setEditStakeholderValue({ ...editStakeholderValue, role: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 font-medium"
                        />
                        <textarea
                          value={editStakeholderValue.responsibility}
                          onChange={(e) => setEditStakeholderValue({ ...editStakeholderValue, responsibility: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm resize-none"
                          rows={2}
                        />
                        <div className="flex space-x-2">
                          <button onClick={saveStakeholderEdit} className="text-green-600 text-sm">
                            <Save className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingStakeholder(null)} className="text-gray-400 text-sm">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between">
                          <h4 className="font-medium text-super-dark-purple mb-2 pr-6">
                            <TerminologyTooltip text={role || ''} />
                          </h4>
                          <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => startEditStakeholder(role)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Edit stakeholder"
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => deleteStakeholder(role)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete stakeholder"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          <TerminologyTooltip text={String(responsibility || '')} />
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No stakeholder responsibilities identified.</p>
            )}
          </div>
        )}
      </div>

      {/* Systems & Platforms */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-orange-50 to-white border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Settings className="w-5 h-5 text-orange-500" />
            <h3 className="text-lg font-semibold">Systems & Platforms</h3>
            <Tooltip text={`Systems and Platforms are the technology components, applications, and infrastructure involved in executing this process.\n\nIncludes:\n- Core business systems and applications\n- Integration platforms and middleware\n- Database systems and data sources\n- External vendor systems\n- Monitoring and reporting tools\n- Security and compliance platforms\n\nThis helps identify system dependencies, integration points, and technology risks.`} placement="top">
              <Info className="w-4 h-4 text-gray-500 cursor-pointer" aria-label="Systems & Platforms Info" />
            </Tooltip>
          </div>
          <div className="flex items-center space-x-2">
            {selectedSystems.length > 1 && (
              <button
                onClick={mergeSystems}
                className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-md text-sm flex items-center space-x-1"
                title={`Merge ${selectedSystems.length} selected systems`}
              >
                <Merge className="w-4 h-4" />
                <span>Merge ({selectedSystems.length})</span>
              </button>
            )}
            <button
              onClick={() => setShowAddSystem(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded-md text-sm flex items-center space-x-1"
              title="Add new system"
            >
              <Plus className="w-4 h-4" />
              <span>Add System</span>
            </button>
            <button
              onClick={() => setIsSystemsCollapsed(!isSystemsCollapsed)}
              className={`p-2 rounded-full border-2 border-orange-500 bg-white shadow-lg hover:bg-orange-500 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ml-2 flex items-center justify-center`}
              style={{ minWidth: 40, minHeight: 40 }}
              title={isSystemsCollapsed ? 'Expand Systems' : 'Collapse Systems'}
            >
              {isSystemsCollapsed ? <ChevronDown className="w-7 h-7" /> : <ChevronUp className="w-7 h-7" />}
            </button>
          </div>
        </div>
        {!isSystemsCollapsed && (
          <div className="p-6">
            {/* Add System Form */}
            {showAddSystem && (
              <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200">
                <h4 className="font-medium mb-3">Add New System</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="System Name"
                    value={newSystem.system}
                    onChange={(e) => setNewSystem({ ...newSystem, system: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Purpose/Description"
                    value={newSystem.purpose}
                    onChange={(e) => setNewSystem({ ...newSystem, purpose: e.target.value })}
                    className="border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div className="flex space-x-2 mt-3">
                  <button onClick={addSystem} className="bg-green-600 text-white px-3 py-1 rounded text-sm">
                    Add
                  </button>
                  <button onClick={() => setShowAddSystem(false)} className="bg-gray-400 text-white px-3 py-1 rounded text-sm">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {Object.keys(systems).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(systems).map(([system, purpose]: [string, any]) => (
                  <div key={system} className={`bg-white p-4 rounded-lg border-l-4 border-orange-500 group relative ${selectedSystems.includes(system) ? 'ring-2 ring-purple-500' : ''
                    }`}>
                    <input
                      type="checkbox"
                      checked={selectedSystems.includes(system)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSystems([...selectedSystems, system]);
                        } else {
                          setSelectedSystems(selectedSystems.filter(s => s !== system));
                        }
                      }}
                      className="absolute top-2 right-2 w-4 h-4"
                    />

                    {editingSystem === system ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editSystemValue.system}
                          onChange={(e) => setEditSystemValue({ ...editSystemValue, system: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 font-medium"
                        />
                        <textarea
                          value={editSystemValue.purpose}
                          onChange={(e) => setEditSystemValue({ ...editSystemValue, purpose: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm resize-none"
                          rows={2}
                        />
                        <div className="flex space-x-2">
                          <button onClick={saveSystemEdit} className="text-green-600 text-sm">
                            <Save className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingSystem(null)} className="text-gray-400 text-sm">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between">
                          <h4 className="font-medium text-super-dark-purple mb-2 pr-6">
                            <TerminologyTooltip text={system || ''} />
                          </h4>
                          <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => startEditSystem(system)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Edit system"
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => deleteSystem(system)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete system"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          <div className="text-sm text-gray-600">
                            <TerminologyTooltip text={
                              typeof purpose === 'string' ?
                                purpose :
                                typeof purpose === 'object' && purpose !== null ?
                                  (purpose.purpose || purpose.description || purpose.name || JSON.stringify(purpose)) :
                                  String(purpose || 'System component')
                            } />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No systems and platforms identified.</p>
            )}
          </div>
        )}
      </div>

      </>}

      {/* Render StepChatModal for AI Q&A about a process step */}
      {chatStepIdx !== null && typeof chatStepIdx === 'number' && chatStepIdx < steps.length && (() => {
        const stepVal = steps[chatStepIdx as number];
        let stepStr = '';
        if (typeof stepVal === 'string') {
          stepStr = stepVal;
        } else if (isStepObj(stepVal)) {
          stepStr = stepVal.step;
        }
        return (
          <EnhancedStepChatModal
            open={chatOpen}
            onClose={() => setChatOpen(false)}
            step={stepStr}
            projectId={synthesis?.project_id ?? undefined}
            stepIndex={chatStepIdx as number}
            onStepsExpanded={() => { }}
            onStepsUpdated={onRefresh}
          />
        );
      })()}
      {/* SOP Type Tabs */}
      {allSOPs.length > 1 && (
        <div className="mb-6 border-b border-gray-200">
          <div className="flex space-x-1">
            {/* Basic SOP Tab */}
            {basicSOPs.length > 0 && (
              <button
                onClick={() => setActiveSOPTab('basic')}
                className={`px-6 py-3 font-medium text-sm transition-all ${
                  activeSOPTab === 'basic'
                    ? 'border-b-2 border-electric-blue text-electric-blue bg-blue-50'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                Basic SOP
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
                  {basicSOPs.length}
                </span>
              </button>
            )}

            {/* Enhanced SOP Tab */}
            {enhancedSOPs.length > 0 && (
              <button
                onClick={() => setActiveSOPTab('enhanced')}
                className={`px-6 py-3 font-medium text-sm transition-all ${
                  activeSOPTab === 'enhanced'
                    ? 'border-b-2 border-purple-500 text-purple-600 bg-purple-50'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                Enhanced SOP
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
                  {enhancedSOPs.length}
                </span>
              </button>
            )}

            {/* Process Map SOP Tab */}
            {processMapSOPs.length > 0 && (
              <button
                onClick={() => setActiveSOPTab('process_map')}
                className={`px-6 py-3 font-medium text-sm transition-all ${
                  activeSOPTab === 'process_map'
                    ? 'border-b-2 border-green-500 text-green-600 bg-green-50'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                Process Map SOP
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
                  {processMapSOPs.length}
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SOPGenerationTab; 